import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import { useAuth } from "../../auth/AuthProvider";
import "./mcImportEngine.css";
import "./mcPeople.css";
import { buildDefaultState, loadEngineState, loadSessionRecords, loadArchivedSessionRecords } from "../utils/mcEngineData";
import {
  buildStoreOptions,
  canManageProfiles,
  ensureAdminProfile,
  formatStoreLabel,
  getProfileAllowedSiteIds,
  getProfileAllowedSectionIds,
  formatProfileAllowedSections,
  getCurrentProfile,
  loadProfiles,
  normalizeStoreValue,
  saveProfiles,
} from "../utils/profileAccess";
import { MC_SECTION_EVENT, activeSections, formatSectionLabel, normalizeSectionId } from "../utils/mcSections";
import { loadPeople } from "../utils/mcPeople";
import { loadSites, saveSites, MC_SITE_EVENT } from "../utils/mcOrgData";
import { mcGetItem, mcSetItem, mcDeleteProfile, getMcCloudSyncState, MC_SYNC_READY_EVENT } from "../utils/mcCloudSync";
import { ESCALATION_ROLES, loadRoleEscalationPolicy, refreshRoleEscalationPolicy, updateRoleEscalationPolicy, saveRoleEscalationPolicy, MC_ROLE_ESCALATION_EVENT } from "../utils/mcEscalationPolicy";
import { loadResponseEvents, MC_RESPONSE_EVENT } from "../utils/mcResponseEvents";
import { loadActivityEvents, recordActivity, MC_ACTIVITY_EVENT } from "../utils/mcActivityEvents";
import { saveResponseNotifications, loadResponseNotifications, deliverEmailNotification, loadEmailDeliveryConfig } from "../utils/mcNotifications";
import {
  ExecutiveSummary,
  OperationalHealth,
  AlertFlow,
  ActionCentre,
  ExecutiveActions,
  RiskRadar,
  Forecast,
} from "./People/Intelligence";

function isExecutiveReviewer(profile = {}) {
  const access = String(profile?.accessLevel || profile?.role || "").trim().toLowerCase();
  const operational = String(profileOperationalRole(profile) || "").trim().toLowerCase();
  return profile?.isPrimaryOwner === true || profile?.primaryOwner === true || access === "owner" || access === "admin" || access === "administrator" || operational === "owner";
}

function findProfileByIdentity(profiles = [], identity = "") {
  const needle = String(identity || "").trim().toLowerCase();
  if (!needle) return null;
  return profiles.find((profile) => [fullName(profile), profile?.email].filter(Boolean).some((value) => String(value).trim().toLowerCase() === needle)) || null;
}

function executiveActionEmailHtml({ heading, message, action, decisionLabel, reviewerName }) {
  return `<div style="background:#eef7fb;padding:28px;font-family:Arial,sans-serif;color:#0b2841"><div style="max-width:680px;margin:auto;background:#fff;border:1px solid #b7dce8;border-radius:18px;overflow:hidden"><div style="padding:24px 28px;background:#071426;color:#fff"><div style="color:#67e8f9;font-size:12px;letter-spacing:.14em;text-transform:uppercase;font-weight:800">MatrixOS · Operational Intelligence</div><h1 style="font-size:25px;margin:10px 0 0">${heading}</h1></div><div style="padding:28px"><p style="font-size:16px;line-height:1.65;margin-top:0">${message}</p><div style="border-left:4px solid #06b6d4;background:#f0fbff;padding:16px 18px;border-radius:10px"><strong style="display:block;font-size:18px">${action.title}</strong><span style="display:block;margin-top:7px;color:#49647c">${action.affectedSites?.length || 0} affected site${action.affectedSites?.length === 1 ? "" : "s"} · ${decisionLabel}</span></div>${reviewerName ? `<p style="margin:22px 0 0;color:#49647c">Action taken by <strong>${reviewerName}</strong>.</p>` : ""}<p style="margin:24px 0 0"><a href="${window.location.origin}/morpheus-count/profiles#executive-actions" style="display:inline-block;background:#06b6d4;color:#fff;text-decoration:none;padding:12px 18px;border-radius:10px;font-weight:800">Open Executive Actions</a></p></div><div style="padding:16px 28px;background:#f7fbfd;border-top:1px solid #dbeaf0;color:#6b8295;font-size:12px">This email exists because a person took operational action in MatrixOS. Operate with Clarity.</div></div></div>`;
}

async function sendExecutiveActionEmail({ to, recipientName, subject, text, html, actionId }) {
  if (!to) return { ok: false, reason: "no_recipient" };
  const notification = {
    id: `exec-action-email-${actionId}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
    eventId: actionId,
    channel: "email",
    recipient: to,
    recipientName: recipientName || to,
    recipientRole: "Executive action participant",
    subject,
    message: text,
    messageHtml: html,
    status: "queued",
    createdAt: new Date().toISOString(),
    queuedAt: new Date().toISOString(),
  };
  saveResponseNotifications([notification, ...loadResponseNotifications()]);
  return deliverEmailNotification(notification, loadEmailDeliveryConfig(), "MatrixOS · Executive Actions");
}

function blankForm() {
  return {
    name: "",
    surname: "",
    email: "",
    whatsapp: "",
    operationalRole: "Stock Controller",
    store: "",
    allowedSiteIds: [],
    assignedSectionIds: [],
    pin: "",
    canFinalizeCount: false,
  };
}

function fullName(profile) {
  return [profile?.name, profile?.surname].filter(Boolean).join(" ").trim() || "Profile";
}

function countProfiles(grouped) {
  return Object.values(grouped).reduce((sum, items) => sum + items.length, 0);
}

function countUniqueOperators(profiles = []) {
  const ids = new Set();
  profiles
    .filter((item) => String(item.role || "").toLowerCase() !== "admin")
    .forEach((item) => {
      const key = String(item.id || `${item.name || ""}-${item.surname || ""}-${item.pin || ""}`).trim().toLowerCase();
      if (key) ids.add(key);
    });
  return ids.size;
}

function siteAccessLabel(profile) {
  const allowed = getProfileAllowedSiteIds(profile).filter((site) => site !== 'ALL');
  if (!allowed.length) {
    const site = normalizeStoreValue(profile.store);
    return site ? formatStoreLabel(site) : "No site assigned";
  }
  if (allowed.length === 1) return formatStoreLabel(allowed[0]);
  return `${allowed.length} sites`;
}

function sectionAccessLabel(profile, sections = []) {
  return formatProfileAllowedSections(profile, sections);
}

function selectedSiteIds(profile) {
  return getProfileAllowedSiteIds(profile).filter((site) => site !== 'ALL');
}

function siteAccessSummary(profile) {
  const allowed = selectedSiteIds(profile);
  if (!allowed.length) return siteAccessLabel(profile);
  if (allowed.length === 1) return formatStoreLabel(allowed[0]);
  return `${allowed.length} sites selected`;
}

function selectedSectionIds(profile) {
  return getProfileAllowedSectionIds(profile).filter((section) => section !== 'ALL');
}

function sectionAccessSummary(profile, sections = []) {
  const assigned = selectedSectionIds(profile);
  if (!assigned.length) return sectionAccessLabel(profile, sections);
  if (assigned.length === 1) return formatSectionLabel(assigned[0], sections);
  return `${assigned.length} sections selected`;
}

function groupLabel(site) {
  if (site === "__ADMINS__") return "Admins";
  return site === "__UNASSIGNED__" ? "No site assigned" : formatStoreLabel(site);
}

function toggleSiteInList(list = [], siteId = '') {
  const normalized = normalizeStoreValue(siteId);
  if (!normalized) return list || [];
  const current = Array.from(new Set((list || []).map(normalizeStoreValue).filter(Boolean)));
  if (current.includes(normalized)) return current.filter((item) => item !== normalized);
  return [...current, normalized];
}

function toggleSectionInList(list = [], sectionId = '') {
  const normalized = normalizeSectionId(sectionId);
  if (!normalized) return list || [];
  const current = Array.from(new Set((list || []).map(normalizeSectionId).filter(Boolean)));
  if (current.includes(normalized)) return current.filter((item) => item !== normalized);
  return [...current, normalized];
}

const PROFILE_ROW_GRID = { gridTemplateColumns: "minmax(260px, 1.25fr) minmax(180px, .8fr) minmax(180px, .8fr) minmax(120px, .5fr) minmax(150px, .65fr) 150px" };
const PEOPLE_TABS = [
  { key: "directory", label: "Directory", description: "Identity, roles, sites and operational responsibility" },
  { key: "permissions", label: "Permissions", description: "Module and action access" },
  { key: "escalation", label: "Escalation", description: "Response order, channels and timeframes" },
  { key: "activity", label: "Activity", description: "Operational events and accountability" },
  { key: "intelligence", label: "Intelligence", description: "Executive people and response health" },
];

function PeopleSubNav({ activeTab, onChange }) {
  return (
    <nav className="mc-peopleSubNav" aria-label="People workspace sections">
      {PEOPLE_TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`mc-peopleSubNav__item ${activeTab === tab.key ? "is-active" : ""}`}
          aria-current={activeTab === tab.key ? "page" : undefined}
          onClick={() => onChange(tab.key)}
        >
          <strong>{tab.label}</strong>
          <span>{tab.description}</span>
        </button>
      ))}
    </nav>
  );
}

function MatrixTooltip({ label, children }) {
  return <span className="mc-matrixTooltip" tabIndex={0} aria-label={label}>
    {children}
    <span className="mc-matrixTooltip__content" role="tooltip">{label}</span>
  </span>;
}

function isHiddenSystemIdentity(profile = {}) {
  const email = String(profile?.email || profile?.userEmail || "").trim().toLowerCase();
  return profile?.isPrimaryOwner === true || profile?.primaryOwner === true || email === "info@invoicemate.co.za";
}

function SearchableAssignment({ people, value, label, onChange }) {
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return people;
    return people.filter((person) => [fullName(person), person.email, person.whatsapp, profileOperationalRole(person)]
      .filter(Boolean).join(" ").toLowerCase().includes(term));
  }, [people, query]);
  const selected = people.find((person) => person.id === value);
  const options = selected && !filtered.some((person) => person.id === selected.id) ? [selected, ...filtered] : filtered;
  return <div className="mc-responseAssignmentPicker">
    <label><span>Search {label}</span><input type="search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`} /></label>
    <label><span>{label}</span><select value={value || ""} onChange={(e)=>onChange(e.target.value)} title={selected ? `${fullName(selected)} • ${selected.email || "No email"}` : `No ${label} assigned`}><option value="">Unassigned</option>{options.map((person)=><option key={person.id} value={person.id}>{fullName(person)} · {profileOperationalRole(person)}</option>)}</select></label>
  </div>;
}

function ChannelToggle({ channels = [], channel, onChange, disabled = false }) {
  const active = channels.includes(channel);
  return <button type="button" disabled={disabled} className={`mc-roleChannel ${active ? "is-active" : ""}`} onClick={() => onChange(active ? channels.filter((item) => item !== channel) : [...channels, channel])}>
    <span className="mc-roleChannel__dot" />{channel === "whatsapp" ? "WhatsApp" : "Email"}
  </button>;
}

function EscalationPeopleWorkspace({ profiles }) {
  const [sites, setSites] = useState(() => loadSites());
  const [policy, setPolicy] = useState(() => loadRoleEscalationPolicy());
  const [savedAt, setSavedAt] = useState("");
  const [saveStatus, setSaveStatus] = useState("loading");
  const [saveError, setSaveError] = useState("");
  const [sharedOrgId, setSharedOrgId] = useState("");
  const savingRef = useRef(false);
  const readSequence = useRef(0);
  const refreshingRef = useRef(false);
  const loadedOrgRef = useRef("");
  const localRecoveryPolicy = useRef(loadRoleEscalationPolicy());

  async function refreshSharedPolicy(force = false) {
    if (savingRef.current || refreshingRef.current) return;
    const readyState = getMcCloudSyncState();
    if (!readyState?.ready || !readyState?.dataHydrated || !readyState?.orgId) {
      setSaveStatus("loading");
      return;
    }
    if (!force && loadedOrgRef.current === readyState.orgId) return;
    refreshingRef.current = true;
    const sequence = ++readSequence.current;
    setSaveStatus("loading");
    setSaveError("");
    try {
      const result = await refreshRoleEscalationPolicy();
      if (sequence !== readSequence.current) return;
      setSharedOrgId(result.orgId);
      loadedOrgRef.current = result.orgId;
      if (result.exists) {
        setPolicy(result.policy);
        setSaveStatus("ready");
        localRecoveryPolicy.current = result.policy;
      } else {
        // Legacy local settings may never have reached Firestore. Allow the
        // operator to explicitly publish them, without automatically replacing
        // a newer server policy or publishing defaults from a second device.
        setPolicy(localRecoveryPolicy.current);
        setSaveStatus("missing");
      }
    } catch (error) {
      if (sequence !== readSequence.current) return;
      setSaveStatus("error");
      setSaveError(error?.message || String(error));
    } finally {
      refreshingRef.current = false;
    }
  }

  useEffect(() => {
    let active = true;
    const syncSites = () => { if (active) setSites(loadSites()); };
    const syncPolicy = () => {
      if (active && !savingRef.current) setPolicy(loadRoleEscalationPolicy());
    };
    const onReady = () => { if (active && !savingRef.current) void refreshSharedPolicy(false); };
    window.addEventListener(MC_SITE_EVENT, syncSites);
    window.addEventListener("mc-cloud-sync", syncSites);
    window.addEventListener(MC_ROLE_ESCALATION_EVENT, syncPolicy);
    window.addEventListener(MC_SYNC_READY_EVENT, onReady);
    void refreshSharedPolicy(false);
    return () => {
      active = false;
      readSequence.current += 1;
      window.removeEventListener(MC_SITE_EVENT, syncSites);
      window.removeEventListener("mc-cloud-sync", syncSites);
      window.removeEventListener(MC_ROLE_ESCALATION_EVENT, syncPolicy);
      window.removeEventListener(MC_SYNC_READY_EVENT, onReady);
    };
  }, []);

  const profileById = useMemo(() => new Map(profiles.map((profile) => [profile.id, profile])), [profiles]);
  const activeSites = useMemo(() => sites.filter((site) => site.active !== false), [sites]);

  const roleAssignments = useMemo(() => ESCALATION_ROLES.reduce((result, role) => {
    const peopleMap = new Map();
    activeSites.forEach((site) => {
      const personId = site?.[role.assignmentKey];
      const person = personId ? profileById.get(personId) : null;
      if (!person) return;
      const current = peopleMap.get(person.id) || { person, sites: [] };
      current.sites.push(site.name || formatStoreLabel(site.siteId));
      peopleMap.set(person.id, current);
    });
    result[role.key] = Array.from(peopleMap.values()).sort((a, b) => fullName(a.person).localeCompare(fullName(b.person)));
    return result;
  }, {}), [activeSites, profileById]);

  async function updateRole(roleKey, patch) {
    if (savingRef.current || saveStatus !== "ready" || !sharedOrgId) return;
    savingRef.current = true;
    readSequence.current += 1;
    setSaveStatus("saving");
    setSaveError("");
    const previous = policy[roleKey] || {};
    try {
      const result = await updateRoleEscalationPolicy(roleKey, patch, sharedOrgId);
      setPolicy(result.policy);
      setSaveStatus("ready");
      const role = ESCALATION_ROLES.find((item) => item.key === roleKey);
      try {
        recordActivity({category:'People',action:'Escalation policy changed',title:`Escalation updated · ${role?.label || roleKey}`,description:'Response timing or channels confirmed in Firestore.',targetId:roleKey,targetName:role?.label || roleKey,metadata:{before:previous,after:result.policy[roleKey]}});
      } catch (activityError) { console.warn("Escalation activity history could not be recorded", activityError); }
      setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error?.message || String(error));
    } finally {
      savingRef.current = false;
    }
  }

  async function publishLocalPolicy() {
    if (savingRef.current || saveStatus !== "missing" || !sharedOrgId) return;
    savingRef.current = true;
    setSaveStatus("saving");
    setSaveError("");
    try {
      const result = await saveRoleEscalationPolicy(localRecoveryPolicy.current, sharedOrgId);
      setPolicy(result.policy);
      setSaveStatus("ready");
      setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error?.message || String(error));
    } finally {
      savingRef.current = false;
    }
  }

  const configuredStages = ESCALATION_ROLES.filter((role) => (roleAssignments[role.key] || []).length).length;
  const assignedPeople = new Set();
  Object.values(roleAssignments).flat().forEach((entry) => assignedPeople.add(entry.person.id));

  return <section className="mc-roleEscalationWorkspace">
    <article className="mc-roleEscalationHero">
      <div>
        <div className="mc-enginePage__eyebrow">Response Designed</div>
        <h2>Escalation</h2>
        <p>Sites decides who holds each responsibility. Escalation decides when each role is alerted, which channels are used, and the minimum response severity for Operations and Owner.</p>
      </div>
      <div className="mc-roleEscalationSaved">
        <span className="mc-roleEscalationSaved__pulse" />
        <strong>{saveStatus === "ready" ? "Shared policy · Firestore" : saveStatus === "saving" ? "Saving policy..." : saveStatus === "loading" ? "Loading shared policy..." : saveStatus === "missing" ? "Local-only policy" : "Shared policy unavailable"}</strong>
        <small>{saveStatus === "ready" ? (savedAt ? `Confirmed ${savedAt}` : "Changes require server confirmation") : saveStatus === "missing" ? "Not yet published to the organisation" : saveStatus === "error" ? "Not saved · check below" : "Please wait"}</small>
        <div style={{gridColumn:"1 / -1", marginTop:8, display:"flex", gap:8, flexWrap:"wrap"}}>
          {saveStatus === "missing" ? <button type="button" onClick={publishLocalPolicy}>Publish local settings</button> : null}
          {saveStatus !== "saving" ? <button type="button" onClick={() => void refreshSharedPolicy(true)}>Refresh shared policy</button> : null}
        </div>
      </div>
    </article>

    {saveError ? <p role="alert" style={{color:"#ff9c9c",margin:"8px 0 14px"}}>Escalation policy was not confirmed: {saveError}. Refresh shared policy and retry.</p> : null}
    {saveStatus === "missing" ? <p role="status" style={{color:"#ffe2a2",margin:"8px 0 14px"}}>Firestore has no shared escalation policy yet. Check the timings below, then publish this browser's settings. This will not overwrite an existing cloud policy.</p> : null}
    <section className="mc-responseFlow" aria-label="Response escalation flow">
      <div className="mc-responseFlow__title"><span>Organisation response flow</span><strong>One policy. Every site.</strong></div>
      <div className="mc-responseFlow__track">
        {ESCALATION_ROLES.map((role, index) => {
          const rolePolicy = policy[role.key] || { delayMinutes: role.defaultDelay, channels: ["email", "whatsapp"] };
          const count = (roleAssignments[role.key] || []).length;
          return <React.Fragment key={role.key}>
            <article className={`mc-responseFlow__stage ${index === 0 ? "is-first" : ""}`}>
              <span>Stage {role.stage}</span>
              <strong>{role.label}</strong>
              <small>{rolePolicy.delayMinutes === 0 ? "Immediately" : `After ${rolePolicy.delayMinutes} min`} · {count} {count === 1 ? "person" : "people"}{role.key !== "siteManager" ? ` · ${rolePolicy.minSeverity && rolePolicy.minSeverity !== "default" ? `${rolePolicy.minSeverity[0].toUpperCase()}${rolePolicy.minSeverity.slice(1)}+` : "Config minimum"}` : ""}</small>
            </article>
            {index < ESCALATION_ROLES.length - 1 ? <div className="mc-responseFlow__arrow">→</div> : null}
          </React.Fragment>;
        })}
      </div>
    </section>

    <div className="mc-roleEscalationSummary">
      <article><span>Stages configured</span><strong>{configuredStages} / {ESCALATION_ROLES.length}</strong><small>Roles currently assigned in Sites</small></article>
      <article><span>People in chain</span><strong>{assignedPeople.size}</strong><small>Read directly from Sites</small></article>
      <article><span>Sites protected</span><strong>{activeSites.length}</strong><small>Using the same role policy</small></article>
    </div>

    <div className="mc-rolePolicyList">
      {ESCALATION_ROLES.map((role) => {
        const rolePolicy = policy[role.key] || { delayMinutes: role.defaultDelay, channels: ["email", "whatsapp"] };
        const assignments = roleAssignments[role.key] || [];
        return <article className="mc-rolePolicyCard" key={role.key}>
          <header className="mc-rolePolicyCard__head">
            <div className="mc-rolePolicyCard__stage">{String(role.stage).padStart(2, "0")}</div>
            <div>
              <span>Response role</span>
              <h3>{role.label}</h3>
              <p>{role.stage === 1 ? "First operational response for the site." : `Notified when the response remains open beyond the ${role.label.toLowerCase()} threshold.`}</p>
            </div>
            <div className="mc-rolePolicyCard__count"><strong>{assignments.length}</strong><span>{assignments.length === 1 ? "person" : "people"}</span></div>
          </header>

          <div className="mc-rolePolicyCard__body">
            <section className="mc-rolePolicyCard__people">
              <div className="mc-rolePolicyCard__sectionTitle"><span>People currently assigned</span><small>Derived from Sites · read only</small></div>
              {assignments.length ? <div className="mc-rolePeopleTable">
                {assignments.map(({ person, sites: assignedSites }) => <div className="mc-rolePeopleTable__row" key={person.id}>
                  <div className="mc-rolePeopleTable__identity"><strong>{fullName(person)}</strong><span>{person.email || "No email"}{person.whatsapp ? ` · ${person.whatsapp}` : ""}</span></div>
                  <div className="mc-rolePeopleTable__sites"><span>{assignedSites.length === activeSites.length ? "All Sites" : `${assignedSites.length} ${assignedSites.length === 1 ? "site" : "sites"}`}</span><small>{assignedSites.slice(0, 3).join(" · ")}{assignedSites.length > 3 ? ` +${assignedSites.length - 3}` : ""}</small></div>
                </div>)}
              </div> : <div className="mc-rolePolicyCard__empty">No {role.label.toLowerCase()} is currently assigned in Sites.</div>}
            </section>

            <section className="mc-rolePolicyCard__controls">
              <label className="mc-roleDelayField">
                <span>Notify this role</span>
                <select disabled={saveStatus !== "ready"} value={String(rolePolicy.delayMinutes)} onChange={(event) => void updateRole(role.key, { delayMinutes: Math.max(0, Number(event.target.value) || 0) })}>
                  <option value="0">Immediately</option>
                  <option value="5">After 5 minutes</option>
                  <option value="10">After 10 minutes</option>
                  <option value="15">After 15 minutes</option>
                  <option value="30">After 30 minutes</option>
                  <option value="45">After 45 minutes</option>
                  <option value="60">After 60 minutes</option>
                  <option value="90">After 90 minutes</option>
                  <option value="120">After 120 minutes</option>
                  <option value="180">After 180 minutes</option>
                  <option value="240">After 240 minutes</option>
                </select>
                <small>{rolePolicy.delayMinutes === 0 ? "Receives the alert as soon as Response Designed is created." : `Receives the alert only if the response is still unresolved after ${rolePolicy.delayMinutes} minutes.`}</small>
              </label>
              {role.key !== "siteManager" ? <label className="mc-roleDelayField">
                <span>Start alerting from severity</span>
                <select disabled={saveStatus !== "ready"} value={rolePolicy.minSeverity || "default"} onChange={(event) => void updateRole(role.key, { minSeverity: event.target.value })}>
                  <option value="default">Follow Config minimum (all responses)</option>
                  <option value="low">Low and above</option>
                  <option value="medium">Medium and above</option>
                  <option value="high">High and Critical only</option>
                  <option value="critical">Critical only</option>
                </select>
                <small>{!rolePolicy.minSeverity || rolePolicy.minSeverity === "default"
                  ? "No extra filter. All responses created by the Minimum response trigger in Config can escalate to this role."
                  : `Only ${rolePolicy.minSeverity === "critical" ? "Critical" : rolePolicy.minSeverity === "high" ? "High or Critical" : rolePolicy.minSeverity === "medium" ? "Medium, High or Critical" : "Low, Medium, High or Critical"} responses can escalate to this role.`} New responses use this setting; pending unsent escalations are rechecked before delivery.</small>
              </label> : null}
              <div className="mc-roleChannelsField">
                <span>Channels</span>
                <div>
                  <ChannelToggle channels={rolePolicy.channels} channel="email" disabled={saveStatus !== "ready"} onChange={(channels) => void updateRole(role.key, { channels })} />
                  <ChannelToggle channels={rolePolicy.channels} channel="whatsapp" disabled={saveStatus !== "ready"} onChange={(channels) => void updateRole(role.key, { channels })} />
                </div>
                <small>Contact details always come from Directory.</small>
              </div>
            </section>
          </div>
        </article>;
      })}
    </div>

    <aside className="mc-roleEscalationRule">
      <strong>One source of truth</strong>
      <span>Directory owns names and contact details. Sites owns responsibility. Escalation owns timing and channels. Response Designed reads all three.</span>
    </aside>
  </section>;
}

const ACCESS_LEVELS = [
  { value: "owner", label: "Owner", help: "Protected organisation access" },
  { value: "administrator", label: "Administrator", help: "Owner-configured workspace access" },
  { value: "standard", label: "Standard user", help: "Operational access only" },
  { value: "read-only", label: "Read only", help: "View permitted workspaces" },
];
const OPERATIONAL_ROLES = ["Owner", "Operations Manager", "Site Manager", "Supervisor", "Stock Controller", "Auditor", "Custom"];
const STANDARD_DEFAULT_TABS = ["workspace", "imports", "prepared-data", "sessions", "archive"];
const ADMINISTRATOR_DEFAULT_TABS = ["workspace", "imports", "prepared-data", "sessions", "archive", "responses", "notifications", "sections", "sites", "tickets"];
const READ_ONLY_DEFAULT_TABS = ["workspace", "archive"];
function defaultTabsForAccess(level = "standard") {
  if (level === "owner") return MODULE_PERMISSIONS.map(([key]) => key);
  if (level === "administrator") return ADMINISTRATOR_DEFAULT_TABS;
  if (level === "read-only") return READ_ONLY_DEFAULT_TABS;
  return STANDARD_DEFAULT_TABS;
}
const MODULE_PERMISSIONS = [
  ["workspace", "Dashboard"], ["imports", "Import"], ["prepared-data", "Prepared Data"],
  ["sessions", "Sessions"], ["archive", "Archives"], ["responses", "Responses"],
  ["notifications", "Response Intelligence"], ["sites", "Sites"], ["profiles", "People"],
  ["sections", "Sections"], ["billing", "Billing"], ["tickets", "My Tickets"],
  ["config", "Config"], ["mapping", "Mapping"], ["categories", "Inventory Configuration"],
];

function profileAccessLevel(profile = {}) {
  if (profile.isPrimaryOwner === true) return "owner";
  if (profile.accessLevel) return profile.accessLevel;
  return String(profile.role || "").toLowerCase() === "admin" ? "administrator" : "standard";
}
function profileOperationalRole(profile = {}) {
  return profile.operationalRole || (profileAccessLevel(profile) === "owner" ? "Owner" : "Stock Controller");
}
function profileTabs(profile = {}) {
  if (profileAccessLevel(profile) === "owner") return MODULE_PERMISSIONS.map(([key]) => key);
  return Array.isArray(profile.tabs) ? profile.tabs : ["workspace", "imports", "prepared-data", "sessions", "archive", "responses", "notifications"];
}

function IdentitySelector({ profiles, selectedId, onChange }) {
  const [query, setQuery] = useState("");
  const visibleProfiles = useMemo(() => profiles.filter((profile) => !isHiddenSystemIdentity(profile)), [profiles]);
  const filtered = useMemo(() => {
    const term = String(query || "").trim().toLowerCase();
    if (!term) return visibleProfiles;
    return visibleProfiles.filter((profile) => {
      const haystack = [
        fullName(profile),
        profile?.email,
        profileOperationalRole(profile),
        ACCESS_LEVELS.find((item) => item.value === profileAccessLevel(profile))?.label,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [visibleProfiles, query]);

  const selected = visibleProfiles.find((profile) => profile.id === selectedId);
  const options = filtered.some((profile) => profile.id === selectedId)
    ? filtered
    : selected ? [selected, ...filtered] : filtered;

  return <div className="mc-peopleIdentitySelect">
    <label>
      <span>Search person</span>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search name, email or role..."
      />
    </label>
    <label>
      <span>Person</span>
      <select value={selectedId || ""} onChange={(event) => onChange(event.target.value)}>
        {options.map((profile) => <option key={profile.id} value={profile.id}>{fullName(profile)} · {profileOperationalRole(profile)}</option>)}
      </select>
    </label>
    <small>{filtered.length} of {visibleProfiles.length} people shown</small>
  </div>;
}

function RolesPeopleWorkspace({ profiles, updateProfile }) {
  const [selectedId, setSelectedId] = useState(() => profiles.find((profile) => !isHiddenSystemIdentity(profile))?.id || "");
  const [draftAccess, setDraftAccess] = useState("standard");
  const [draftRole, setDraftRole] = useState("Stock Controller");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!profiles.some((profile) => profile.id === selectedId && !isHiddenSystemIdentity(profile))) setSelectedId(profiles.find((profile) => !isHiddenSystemIdentity(profile))?.id || "");
  }, [profiles, selectedId]);

  const selected = profiles.find((profile) => profile.id === selectedId);
  const savedAccess = selected ? profileAccessLevel(selected) : "standard";
  const savedRole = selected ? profileOperationalRole(selected) : "Stock Controller";
  const primaryOwner = selected?.isPrimaryOwner === true;

  useEffect(() => {
    if (!selected) return;
    setDraftAccess(savedAccess);
    setDraftRole(savedRole);
    setMessage("");
  }, [selectedId, savedAccess, savedRole]);

  if (!selected) return null;
  const dirty = draftAccess !== savedAccess || draftRole !== savedRole;

  function chooseAccess(value) {
    if (primaryOwner && value !== "owner") {
      setMessage("Primary Owner Protection prevents this account from being downgraded.");
      return;
    }
    setDraftAccess(value);
    setMessage("");
  }

  function chooseRole(value) {
    if (primaryOwner && value !== "Owner") {
      setMessage("The Primary Owner must retain the Owner operational role.");
      return;
    }
    setDraftRole(value);
    setMessage("");
  }

  function save() {
    if (!dirty) return;
    const nextAccess = primaryOwner ? "owner" : draftAccess;
    updateProfile(selected.id, {
      accessLevel: nextAccess,
      operationalRole: primaryOwner ? "Owner" : draftRole,
      role: primaryOwner || draftAccess === "owner" || draftAccess === "administrator" ? "admin" : "user",
      isPrimaryOwner: primaryOwner,
      tabs: defaultTabsForAccess(nextAccess),
      loginInviteRequired: !primaryOwner && (nextAccess === "administrator" || nextAccess === "owner") && Boolean(selected.email),
    });
    setMessage("Roles saved. Access changes apply the next time this person signs in.");
  }

  function discard() {
    setDraftAccess(savedAccess);
    setDraftRole(savedRole);
    setMessage("Unsaved role changes discarded.");
  }

  return <section className="mc-peopleManager">
    <div className="mc-peopleManager__header"><div><div className="mc-enginePage__eyebrow">Identity intelligence</div><h2>Roles</h2><p>Separate system access from operational responsibility.</p></div><IdentitySelector profiles={profiles} selectedId={selectedId} onChange={setSelectedId}/></div>
    <div className="mc-peopleIdentityHero"><div><span>Selected person</span><strong>{fullName(selected)}</strong><small>{selected.email || "Login profile"}</small></div><div><span>Access level</span><strong>{ACCESS_LEVELS.find((item)=>item.value===draftAccess)?.label || draftAccess}</strong></div><div><span>Operational role</span><strong>{draftRole}</strong></div></div>
    {primaryOwner ? <div className="mc-peopleNotice"><strong>Primary Owner Protection</strong><br/>This account cannot be replaced or downgraded from this screen. Ownership requires a controlled transfer workflow.</div> : null}
    <div className="mc-peopleManager__grid">
      <article className="mc-peopleManager__panel"><h3>Access level</h3><p>Controls what kind of account this is. Only the protected Primary Owner has unrestricted access.</p><div className="mc-choiceGrid">{ACCESS_LEVELS.map((item)=><button key={item.value} type="button" disabled={false} className={draftAccess===item.value?'is-active':''} onClick={()=>chooseAccess(item.value)}><strong>{item.label}</strong><span>{item.value === "owner" && !primaryOwner ? "Organisation owner access · Primary Owner remains protected" : item.help}</span></button>)}</div></article>
      <article className="mc-peopleManager__panel"><h3>Operational role</h3><p>Defines responsibility for verification, escalation and accountability.</p><div className="mc-choiceGrid">{OPERATIONAL_ROLES.map((item)=><button key={item} type="button" disabled={false} className={draftRole===item?'is-active':''} onClick={()=>chooseRole(item)}><strong>{item}</strong><span>{item==='Owner' ? (primaryOwner ? 'Primary organisation responsibility' : 'Organisation responsibility') : 'Operational responsibility'}</span></button>)}</div></article>
    </div>
    <div className="mc-safePermissionActions mc-roleSaveActions"><div>{dirty?<span className="mc-unsavedBadge">Unsaved role changes</span>:<span className="mc-savedBadge">No unsaved changes</span>}{message?<small>{message}</small>:null}</div><div><button type="button" className="mc-engineBtn mc-engineBtn--ghost" disabled={!dirty} onClick={discard}>Discard</button><button type="button" className="mc-engineBtn mc-engineBtn--primary" disabled={!dirty} onClick={save}>Save roles</button></div></div>
  </section>;
}


function activityTimestamp(value) {
  const date = new Date(value || 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

function activityPersonName(item = {}) {
  return String(item.personName || item.by || item.actor || item.user || item.startedBy || item.completedBy || item.owner || "System").trim() || "System";
}

function buildOperationalActivity() {
  const events = [];
  const add = (event) => {
    const timestamp = activityTimestamp(event.timestamp);
    if (!timestamp) return;
    events.push({
      id: event.id || `activity-${timestamp.getTime()}-${events.length}`,
      timestamp: timestamp.toISOString(),
      category: event.category || "Operations",
      action: event.action || "Updated",
      title: event.title || event.action || "Operational event",
      description: event.description || "",
      person: event.person || "System",
      role: event.role || "",
      site: event.site || "All Sites",
      severity: event.severity || "normal",
      metadata: event.metadata || {},
      source: event.source || "Morpheus",
    });
  };

  loadActivityEvents().forEach((event) => add(event));

  const responseEvents = loadResponseEvents();
  responseEvents.forEach((response, responseIndex) => {
    const site = response.site || response.siteName || response.storeName || "Unassigned site";
    const person = response.completedBy || response.createdBy || response.owner || "System";
    add({
      id: `response-${response.id || response.eventId || responseIndex}`,
      timestamp: response.createdAt || response.timestamp || response.completedAt,
      category: "Response",
      action: "Response created",
      title: `${response.severity ? String(response.severity).replace(/^./, (v) => v.toUpperCase()) + " response" : "Response Designed"}`,
      description: response.sectionName || response.section || response.cycleName || "Variance response created",
      person,
      role: response.createdByRole || "",
      site,
      severity: response.severity || "normal",
      source: "Response Designed",
      metadata: { status: response.status, exposure: response.currentExposure ?? response.exposure, responseId: response.id || response.eventId },
    });
    (Array.isArray(response.timeline) ? response.timeline : []).forEach((entry, index) => add({
      id: `response-${response.id || responseIndex}-timeline-${index}`,
      timestamp: entry.at || entry.timestamp || entry.createdAt,
      category: "Response",
      action: entry.action || entry.title || "Response updated",
      title: entry.action || entry.title || "Response updated",
      description: entry.description || entry.note || "",
      person: activityPersonName(entry),
      role: entry.role || "",
      site,
      severity: response.severity || "normal",
      source: "Response Designed",
      metadata: { ...entry, responseId: response.id || response.eventId },
    }));
  });

  const sessions = [...loadSessionRecords(), ...loadArchivedSessionRecords()];
  const seen = new Set();
  sessions.forEach((session, index) => {
    const sessionId = session.sessionId || session.id || `session-${index}`;
    if (seen.has(sessionId)) return;
    seen.add(sessionId);
    const site = session.storeName || session.store || session.siteName || "Unassigned site";
    const section = session.sectionName || session.section || (session.fullSite ? "Full Count" : "Count session");
    const common = { category: "Count", site, source: "Live Count", metadata: { sessionId, section, cycle: session.cycleName || session.cycle?.name || "" } };
    add({ ...common, id: `${sessionId}-started`, timestamp: session.startedAt || session.createdAt, action: "Session started", title: `Count started · ${section}`, description: session.cycleName || "", person: session.startedBy || session.ownerName || session.userName || "System" });
    add({ ...common, id: `${sessionId}-completed`, timestamp: session.completedAt, action: "Count completed", title: `Count completed · ${section}`, description: `${session.flaggedCount || session.flaggedItems || 0} flagged items`, person: session.completedBy || session.startedBy || "System", severity: Number(session.flaggedCount || session.flaggedItems || 0) > 0 ? "attention" : "normal" });
    add({ ...common, id: `${sessionId}-finalized`, timestamp: session.finalizedAt || session.archivedAt, action: "Session finalized", title: `Count finalized · ${section}`, description: session.finalizedBy ? `Finalized by ${session.finalizedBy}` : "", person: session.finalizedBy || session.completedBy || "System" });
  });

  return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

function formatActivityTime(value) {
  const date = activityTimestamp(value);
  if (!date) return "—";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatActivityDate(value) {
  const date = activityTimestamp(value);
  if (!date) return "Unknown date";
  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const same = (left, right) => left.toDateString() === right.toDateString();
  if (same(date, now)) return "Today";
  if (same(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" });
}

function ActivityPeopleWorkspace({ profiles }) {
  const [events, setEvents] = useState(() => buildOperationalActivity());
  const [period, setPeriod] = useState("7");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    const refresh = () => setEvents(buildOperationalActivity());
    window.addEventListener(MC_RESPONSE_EVENT, refresh);
    window.addEventListener(MC_ACTIVITY_EVENT, refresh);
    window.addEventListener("mc-session-records-updated", refresh);
    window.addEventListener("mc-cloud-sync", refresh);
    return () => {
      window.removeEventListener(MC_RESPONSE_EVENT, refresh);
      window.removeEventListener(MC_ACTIVITY_EVENT, refresh);
      window.removeEventListener("mc-session-records-updated", refresh);
      window.removeEventListener("mc-cloud-sync", refresh);
    };
  }, []);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const cutoff = period === "all" ? 0 : Date.now() - Number(period) * 86400000;
    return events.filter((event) => {
      if (cutoff && new Date(event.timestamp).getTime() < cutoff) return false;
      if (category !== "all" && event.category.toLowerCase() !== category) return false;
      if (!term) return true;
      return [event.title, event.action, event.description, event.person, event.site, event.role, event.category]
        .filter(Boolean).join(" ").toLowerCase().includes(term);
    });
  }, [events, period, query, category]);

  useEffect(() => {
    if (!filtered.length) { setSelectedId(""); return; }
    if (!filtered.some((event) => event.id === selectedId)) setSelectedId(filtered[0].id);
  }, [filtered, selectedId]);

  const selected = filtered.find((event) => event.id === selectedId) || filtered[0];
  const todayEvents = events.filter((event) => formatActivityDate(event.timestamp) === "Today");
  const activeToday = new Set(todayEvents.map((event) => event.person).filter((name) => name && name !== "System")).size;
  const escalations = filtered.filter((event) => /escalat/i.test(`${event.action} ${event.title}`)).length;
  const responses = filtered.filter((event) => event.category === "Response").length;
  const grouped = filtered.reduce((result, event) => {
    const key = formatActivityDate(event.timestamp);
    (result[key] ||= []).push(event);
    return result;
  }, {});

  return <section className="mc-activityWorkspace">
    <article className="mc-activityHero">
      <div>
        <div className="mc-enginePage__eyebrow">Operational memory</div>
        <h2>Activity</h2>
        <p>Replay the actions behind every count, response and operational decision. Activity becomes the source of truth for what happened, who acted and when.</p>
      </div>
      <div className="mc-activityLive"><span /><div><strong>Live timeline</strong><small>Updates as Morpheus changes</small></div></div>
    </article>

    <section className="mc-activityKpis">
      <article><span>People active today</span><strong>{activeToday}</strong><small>Unique people recorded</small></article>
      <article><span>Events in view</span><strong>{filtered.length}</strong><small>{period === "all" ? "All recorded activity" : `Last ${period} days`}</small></article>
      <article><span>Response events</span><strong>{responses}</strong><small>Created, updated and resolved</small></article>
      <article><span>Escalations</span><strong>{escalations}</strong><small>Movement beyond first response</small></article>
    </section>

    <section className="mc-activityCommand">
      <div className="mc-activityModes" aria-label="Activity categories">
        {[['all','All activity'],['response','Response'],['count','Counts'],['people','People'],['access','Access']].map(([key,label]) => <button key={key} type="button" className={category === key ? 'is-active' : ''} onClick={() => setCategory(key)}>{label}</button>)}
      </div>
      <label><span>Period</span><select value={period} onChange={(event) => setPeriod(event.target.value)}><option value="1">Today</option><option value="2">Today & yesterday</option><option value="7">Last 7 days</option><option value="30">Last 30 days</option><option value="all">All activity</option></select></label>
      <label className="mc-activitySearch"><span>Search activity</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Person, site, action or role..." /></label>
    </section>

    <section className="mc-activityBoard">
      <article className="mc-activityFeed">
        <div className="mc-activityPanelHead"><div><span>Operational timeline</span><strong>{filtered.length} events</strong></div><small>Newest first</small></div>
        <div className="mc-activityFeedScroll">
          {!filtered.length ? <div className="mc-emptyState">No operational activity matches these filters.</div> : Object.entries(grouped).map(([date, items]) => <section className="mc-activityDay" key={date}>
            <header><strong>{date}</strong><span>{items.length} events</span></header>
            {items.map((event) => <button type="button" key={event.id} className={`mc-activityEvent ${selected?.id === event.id ? 'is-active' : ''} is-${event.severity}`} onClick={() => setSelectedId(event.id)}>
              <span className="mc-activityEvent__time">{formatActivityTime(event.timestamp)}</span>
              <span className="mc-activityEvent__dot" />
              <span className="mc-activityEvent__body"><strong>{event.title}</strong><small>{event.person} · {event.site}</small></span>
              <span className="mc-activityEvent__category">{event.category}</span>
            </button>)}
          </section>)}
        </div>
      </article>

      <article className="mc-activityDetail">
        {!selected ? <div className="mc-emptyState">Select an activity event.</div> : <>
          <div className="mc-activityPanelHead"><div><span>Selected event</span><strong>{selected.category}</strong></div><span className={`mc-activityStatus is-${selected.severity}`}>{selected.severity === 'normal' ? 'Recorded' : selected.severity}</span></div>
          <div className="mc-activityDetailHero"><span>{formatActivityDate(selected.timestamp)} · {formatActivityTime(selected.timestamp)}</span><h3>{selected.title}</h3><p>{selected.description || "Operational event recorded by Morpheus."}</p></div>
          <div className="mc-activityDetailGrid">
            <article><span>Person</span><strong>{selected.person}</strong><small>{selected.role || "Role not recorded"}</small></article>
            <article><span>Site</span><strong>{selected.site}</strong><small>{selected.source}</small></article>
            <article><span>Action</span><strong>{selected.action}</strong><small>{selected.category}</small></article>
            <article><span>Recorded</span><strong>{new Date(selected.timestamp).toLocaleDateString()}</strong><small>{formatActivityTime(selected.timestamp)}</small></article>
          </div>
          <section className="mc-activityMetadata"><span>Operational context</span>{Object.entries(selected.metadata || {}).filter(([,value]) => value !== undefined && value !== null && value !== '').slice(0,8).map(([key,value]) => <div key={key}><small>{key.replace(/([A-Z])/g,' $1')}</small><strong>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</strong></div>)}</section>
        </>}
      </article>
    </section>
  </section>;
}



const MC_EXECUTIVE_ACTIONS_KEY = "mc-executive-action-workspaces-v1";

function loadExecutiveActionWorkspaces() {
  try {
    const stored = JSON.parse(mcGetItem(MC_EXECUTIVE_ACTIONS_KEY) || "{}");
    return stored && typeof stored === "object" ? stored : {};
  } catch {
    return {};
  }
}

function saveExecutiveActionWorkspace(action) {
  if (!action?.id) return action;
  try {
    const current = loadExecutiveActionWorkspaces();
    const next = { ...current, [action.id]: action };
    const serialised = JSON.stringify(next);
    mcSetItem(MC_EXECUTIVE_ACTIONS_KEY, serialised);
    window.dispatchEvent(new CustomEvent("mc-executive-actions-updated", { detail: action }));
  } catch {}
  return action;
}

function listExecutiveActionWorkspaces() {
  return Object.values(loadExecutiveActionWorkspaces())
    .filter(Boolean)
    .sort((a, b) => new Date(b.updatedAt || b.startedAt || 0).getTime() - new Date(a.updatedAt || a.startedAt || 0).getTime());
}

function actionProgress(action = {}) {
  const tasks = (action.affectedSites || []).flatMap((site) => site.tasks || []);
  const completed = tasks.filter((task) => task.completed).length;
  return { total: tasks.length, completed, percent: tasks.length ? Math.round(completed / tasks.length * 100) : 0 };
}

function actionTaskTitles(label = "") {
  const normalized = String(label).toLowerCase();
  const primary = normalized.includes("site manager")
    ? "Assign the Site Manager in Sites"
    : normalized.includes("operations manager")
      ? "Assign the Operations Manager in Sites"
      : normalized.includes("owner")
        ? "Confirm the Owner assignment in Sites"
        : normalized.includes("response") || normalized.includes("critical")
          ? "Review and resolve the linked Response Designed items"
          : normalized.includes("escalat") || normalized.includes("channel")
            ? "Review the role escalation policy and channels"
            : label || "Complete the recommended operational change";
  return [
    "Review the current operational evidence",
    primary,
    "Confirm the responsible people and contact details",
    "Verify Email and WhatsApp delivery readiness",
    "Record the operational outcome",
  ].filter((item, index, list) => item && list.indexOf(item) === index);
}

function createExecutiveActionWorkspace(action, actor = {}) {
  const now = new Date().toISOString();
  const actorName = fullName(actor) || actor?.email || "Current user";
  return {
    id: action.id,
    sourceActionId: action.id,
    title: action.label,
    status: "in_progress",
    priority: action.priority,
    opportunity: action.opportunity,
    estimatedGain: action.organisationGain,
    estimatedEffort: action.effortMinutes,
    confidence: action.confidence,
    startedAt: now,
    startedBy: actorName,
    updatedAt: now,
    completedAt: null,
    notes: "",
    evidence: [],
    completionOutcome: "",
    completionSummary: "",
    verificationNote: "",
    verifiedBy: null,
    verifiedAt: null,
    affectedSites: (action.sites || []).map((site) => ({
      ...site,
      expanded: false,
      tasks: actionTaskTitles(action.label).map((title, index) => ({
        id: `${action.id}-${site.id}-${index + 1}`,
        title,
        completed: false,
        completedBy: null,
        completedAt: null,
      })),
    })),
    timeline: [
      { id: `${action.id}-started`, timestamp: now, title: "Action started", description: `${actorName} opened the execution workspace.` },
    ],
  };
}

function ExecutiveActionWorkspaceModal({ action, onClose, onChange, profiles = [] }) {
  const [expandedSiteId, setExpandedSiteId] = useState(action?.affectedSites?.[0]?.id || "");
  const [savedMessage, setSavedMessage] = useState("");
  const [completionOpen, setCompletionOpen] = useState(false);
  const [evidencePreview, setEvidencePreview] = useState(null);
  useEffect(() => {
    setExpandedSiteId(action?.affectedSites?.[0]?.id || "");
    setSavedMessage("");
    setCompletionOpen(false);
    setEvidencePreview(null);
  }, [action?.id]);
  useEffect(() => {
    if (!action?.id) return;
    saveExecutiveActionWorkspace(action);
  }, [action]);
  if (!action) return null;

  const tasks = action.affectedSites.flatMap((site) => site.tasks || []);
  const completeCount = tasks.filter((task) => task.completed).length;
  const progress = tasks.length ? Math.round((completeCount / tasks.length) * 100) : 0;
  const remainingMinutes = Math.max(0, Math.round((action.estimatedEffort || 0) * (1 - progress / 100)));
  const isLocked = ["awaiting_verification", "verified"].includes(action.status);
  let currentReviewer = null;
  try { currentReviewer = getCurrentProfile(); } catch {}
  const currentReviewerName = fullName(currentReviewer || {}) || currentReviewer?.email || "Current user";
  const canReview = isExecutiveReviewer(currentReviewer || {}) && String(currentReviewerName).trim().toLowerCase() !== String(action.startedBy || "").trim().toLowerCase();

  function commit(next, message = "Progress saved") {
    const withUpdate = { ...next, updatedAt: new Date().toISOString() };
    saveExecutiveActionWorkspace(withUpdate);
    onChange(withUpdate);
    setSavedMessage(message);
  }

  function pushTimeline(next, title, description) {
    const now = new Date().toISOString();
    return {
      ...next,
      timeline: [{ id: `${action.id}-${title}-${now}`, timestamp: now, title, description }, ...(next.timeline || [])],
    };
  }

  function toggleTask(siteId, taskId) {
    if (isLocked) return;
    const now = new Date().toISOString();
    const nextSites = action.affectedSites.map((site) => site.id !== siteId ? site : {
      ...site,
      tasks: site.tasks.map((task) => task.id !== taskId ? task : {
        ...task,
        completed: !task.completed,
        completedAt: !task.completed ? now : null,
        completedBy: !task.completed ? action.startedBy : null,
      }),
    });
    const changedTask = nextSites.find((site) => site.id === siteId)?.tasks.find((task) => task.id === taskId);
    const next = changedTask?.completed
      ? pushTimeline({ ...action, affectedSites: nextSites }, "Task completed", changedTask.title)
      : { ...action, affectedSites: nextSites };
    commit(next, "Task progress saved");
  }

  function updateNotes(value) {
    if (isLocked) return;
    commit({ ...action, notes: value }, "Notes saved automatically");
  }

  async function addEvidence(files) {
    if (isLocked || !files?.length) return;
    const now = new Date().toISOString();
    const readFile = (file) => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(file);
    });
    const additions = await Promise.all(Array.from(files).map(async (file, index) => ({
      id: `${action.id}-evidence-${Date.now()}-${index}`,
      name: file.name,
      type: file.type || "File",
      size: file.size || 0,
      addedBy: action.startedBy,
      addedAt: now,
      dataUrl: file.size <= 5 * 1024 * 1024 ? await readFile(file) : "",
      previewAvailable: file.size <= 5 * 1024 * 1024,
    })));
    const next = pushTimeline({ ...action, evidence: [...(action.evidence || []), ...additions] }, "Evidence added", `${additions.length} file${additions.length === 1 ? "" : "s"} attached.`);
    commit(next, "Evidence attached");
    recordActivity({
      category: "People",
      action: "Executive action evidence added",
      title: action.title,
      description: `${additions.length} evidence file${additions.length === 1 ? "" : "s"} added.`,
      metadata: { actionId: action.id, files: additions.map((item) => item.name) },
    });
  }

  function removeEvidence(id) {
    if (isLocked) return;
    commit({ ...action, evidence: (action.evidence || []).filter((item) => item.id !== id) }, "Evidence removed");
  }

  function saveProgress() {
    const next = pushTimeline(action, "Progress saved", `${completeCount} of ${tasks.length} tasks complete.`);
    commit(next, "✓ Saved");
    recordActivity({
      category: "People",
      action: "Executive action progress saved",
      title: `${action.title} · ${progress}% complete`,
      description: `${completeCount} of ${tasks.length} tasks complete across ${action.affectedSites.length} sites.`,
      metadata: { actionId: action.id, progress, affectedSites: action.affectedSites.map((site) => site.name) },
    });
  }

  function closeWorkspace() {
    const withUpdate = { ...action, updatedAt: new Date().toISOString() };
    saveExecutiveActionWorkspace(withUpdate);
    onChange(withUpdate);
    onClose();
  }

  async function submitForVerification() {
    if (progress < 100 || !action.completionOutcome) return;
    const now = new Date().toISOString();
    const next = pushTimeline({ ...action, status: "awaiting_verification", completedAt: now }, "Submitted for verification", `${action.startedBy} reported the outcome as ${action.completionOutcome}.`);
    commit(next, "Submitted for verification");
    setCompletionOpen(false);
    recordActivity({
      category: "People",
      action: "Executive action submitted for verification",
      title: action.title,
      description: `Outcome reported as ${action.completionOutcome}.`,
      metadata: { actionId: action.id, outcome: action.completionOutcome, expectedGain: action.estimatedGain },
    });
    const reviewers = profiles.filter((profile) => isExecutiveReviewer(profile) && profile?.email && String(fullName(profile)).trim().toLowerCase() !== String(action.startedBy || "").trim().toLowerCase());
    await Promise.all(reviewers.map((reviewer) => sendExecutiveActionEmail({
      to: reviewer.email, recipientName: fullName(reviewer), actionId: action.id,
      subject: `Action ready for review · ${action.title}`,
      text: `${action.startedBy} submitted ${action.title} for verification. Open MatrixOS Executive Actions to review the evidence, notes and timeline.`,
      html: executiveActionEmailHtml({ heading: "An action is ready for your review", message: `${action.startedBy} completed an operational action and submitted it for independent verification.`, action, decisionLabel: "Awaiting verification", reviewerName: action.startedBy }),
    })));
  }

  async function verifyAction(decision) {
    if ((decision === "more" || decision === "reject") && !String(action.verificationNote || "").trim()) {
      setSavedMessage("Add a reason before recording this decision");
      return;
    }
    const now = new Date().toISOString();
    let verifier = null;
    try { verifier = getCurrentProfile(); } catch {}
    const verifierName = fullName(verifier || {}) || verifier?.email || "Owner / Admin";
    const status = decision === "accept" ? "verified" : decision === "reject" ? "rejected" : "needs_more_work";
    const label = decision === "accept" ? "Action verified" : decision === "reject" ? "Verification rejected" : "More work requested";
    const next = pushTimeline({
      ...action,
      status,
      verifiedAt: decision === "accept" ? now : null,
      verifiedBy: decision === "accept" ? verifierName : null,
      reviewedBy: verifierName,
      reviewedAt: now,
    }, label, action.verificationNote || (decision === "accept" ? "The reported operational outcome was accepted." : "The action requires further operational work."));
    commit(next, label);
    recordActivity({
      category: "People",
      action: label,
      title: action.title,
      description: action.verificationNote || label,
      metadata: { actionId: action.id, decision, outcome: action.completionOutcome, reviewedBy: verifierName },
    });
    const starter = findProfileByIdentity(profiles, action.startedBy);
    const decisionLabel = decision === "accept" ? "Verified" : decision === "more" ? "Needs more work" : "Rejected";
    if (starter?.email) {
      await sendExecutiveActionEmail({
        to: starter.email, recipientName: fullName(starter), actionId: action.id,
        subject: `${decisionLabel} · ${action.title}`,
        text: `${verifierName} reviewed your action “${action.title}”. Decision: ${decisionLabel}. ${action.verificationNote || "Open MatrixOS to review the outcome."}`,
        html: executiveActionEmailHtml({ heading: `Your action was ${decisionLabel.toLowerCase()}`, message: action.verificationNote || `${verifierName} reviewed the operational evidence, notes and timeline and recorded a decision.`, action, decisionLabel, reviewerName: verifierName }),
      });
    }
  }

  const statusLabel = action.status === "verified" ? "Verified" : action.status === "awaiting_verification" ? "Awaiting verification" : action.status === "needs_more_work" ? "Needs more work" : action.status === "rejected" ? "Rejected" : "In progress";

  return <div className="mc-actionWorkspaceBackdrop" role="presentation">
    <section className="mc-actionWorkspaceModal" role="dialog" aria-modal="true" aria-labelledby="mc-action-workspace-title">
      <header className="mc-actionWorkspaceHeader">
        <div><span>Execution workspace</span><h2 id="mc-action-workspace-title">{action.title}</h2><p>{action.opportunity} · {action.affectedSites.length} affected site{action.affectedSites.length === 1 ? "" : "s"}</p></div>
        <div className="mc-actionWorkspaceHeader__status"><small>Status</small><strong className={`is-${action.status}`}>{statusLabel}</strong></div>
      </header>

      <div className="mc-actionWorkspaceScroll">
      <div className="mc-actionWorkspaceMetrics">
        <article><MatrixTooltip label="Expected improvement to organisation health if this action is completed and independently verified."><span>Expected gain ⓘ</span></MatrixTooltip><strong>+{action.estimatedGain || 0} pts</strong><small>Organisation health</small></article>
        <article><MatrixTooltip label="Estimated leadership time required to complete the generated tasks across all affected sites."><span>Estimated effort ⓘ</span></MatrixTooltip><strong>{action.estimatedEffort || 0} min</strong><small>{remainingMinutes} min remaining</small></article>
        <article><span>Started by</span><strong>{action.startedBy}</strong><small>{new Date(action.startedAt).toLocaleString()}</small></article>
        <article><MatrixTooltip label="How strongly the available Live Count, response, people and activity evidence supports this recommendation."><span>Prediction confidence ⓘ</span></MatrixTooltip><strong>{action.confidence || 0}%</strong><small>{action.confidence >= 80 ? "High confidence" : "Developing signal"}</small></article>
      </div>

      <section className="mc-actionWorkspaceProgress">
        <div><span>Action progress</span><strong>{progress}%</strong></div>
        <div className="mc-actionWorkspaceProgress__track"><i style={{ width: `${progress}%` }} /></div>
        <small>{completeCount} of {tasks.length} tasks completed</small>
      </section>

      <div className="mc-actionWorkspaceBody">
        <main className="mc-actionWorkspaceMain">
          <section className="mc-actionWorkspaceObjective"><span>Objective</span><h3>{action.title}</h3><p>Complete the site-specific tasks, attach operational evidence and submit the reported outcome for verification.</p></section>
          <section className="mc-actionWorkspaceSites">
            <div className="mc-actionWorkspaceSectionHead"><div><span>Affected sites</span><h3>Execution checklist</h3></div><strong>{action.affectedSites.length} sites</strong></div>
            {action.affectedSites.map((site) => {
              const siteComplete = (site.tasks || []).filter((task) => task.completed).length;
              const open = expandedSiteId === site.id;
              return <article key={site.id} className={`mc-actionSiteCard ${open ? "is-open" : ""}`}>
                <button type="button" className="mc-actionSiteCard__head" onClick={() => setExpandedSiteId(open ? "" : site.id)}>
                  <span>{open ? "−" : "+"}</span><div><strong>{site.name}</strong><small>Risk {site.risk}% → projected {site.projected}%</small></div><b>{siteComplete}/{site.tasks.length}</b>
                </button>
                {open ? <div className="mc-actionSiteCard__body">
                  <div className="mc-actionSiteFacts"><span><small>Current risk</small><strong>{site.risk}%</strong></span><span><small>Projected risk</small><strong>{site.projected}%</strong></span><span><small>Estimated improvement</small><strong>{Math.max(0, site.risk - site.projected)} pts</strong></span></div>
                  <div className="mc-actionTaskList">{site.tasks.map((task) => <label key={task.id} className={task.completed ? "is-complete" : ""}><input type="checkbox" checked={task.completed} disabled={isLocked} onChange={() => toggleTask(site.id, task.id)} /><span><strong>{task.title}</strong><small>{task.completed ? `Completed by ${task.completedBy}` : "Pending operational action"}</small></span></label>)}</div>
                </div> : null}
              </article>;
            })}
          </section>
        </main>

        <aside className="mc-actionWorkspaceAside">
          <section className="mc-actionEvidence">
            <div className="mc-actionWorkspaceSectionHead"><div><span>Operational proof</span><h3>Evidence</h3></div><strong>{(action.evidence || []).length}</strong></div>
            {!isLocked ? <label className="mc-actionEvidenceUpload"><input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv" onChange={(event) => { addEvidence(event.target.files); event.target.value = ""; }} /><span>Add photos or files</span><small>Stored as action evidence metadata</small></label> : null}
            <div className="mc-actionEvidenceList">{!(action.evidence || []).length ? <p>No evidence attached yet.</p> : action.evidence.map((item) => <article key={item.id}><button type="button" className="mc-actionEvidenceOpen" onClick={() => setEvidencePreview(item)}><strong>{item.name}</strong><small>{Math.max(1, Math.round((item.size || 0) / 1024))} KB · {item.addedBy}</small><em>{item.dataUrl ? "View evidence" : "Details"}</em></button>{!isLocked ? <button type="button" className="mc-actionEvidenceRemove" onClick={() => removeEvidence(item.id)}>Remove</button> : null}</article>)}</div>
          </section>
          <section className="mc-actionTimeline"><div className="mc-actionWorkspaceSectionHead"><div><span>Action history</span><h3>Timeline</h3></div></div><div>{action.timeline.map((event) => <article key={event.id}><time>{new Date(event.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time><div><strong>{event.title}</strong><small>{event.description}</small></div></article>)}</div></section>
          <section className="mc-actionNotes"><div className="mc-actionWorkspaceSectionHead"><div><span>Operational context</span><h3>Notes</h3></div></div><textarea value={action.notes || ""} disabled={isLocked} onChange={(event) => updateNotes(event.target.value)} placeholder="Record observations, decisions and outcomes..." /><small>{savedMessage || "Notes and task progress save automatically."}</small></section>

          {progress === 100 && action.status === "in_progress" ? <section className="mc-actionCompletion">
            <div className="mc-actionWorkspaceSectionHead"><div><span>Reported result</span><h3>Complete and submit</h3></div></div>
            <div className="mc-actionOutcomeChoices">{[["yes","Yes"],["partial","Partially"],["no","No"]].map(([value,label]) => <button key={value} type="button" className={action.completionOutcome === value ? "is-active" : ""} onClick={() => commit({ ...action, completionOutcome: value }, "Outcome selected")}>{label}</button>)}</div>
            <textarea value={action.completionSummary || ""} onChange={(event) => commit({ ...action, completionSummary: event.target.value }, "Outcome summary saved")} placeholder="Summarise what changed and any remaining risk..." />
            <button type="button" className="mc-actionSubmitVerification" disabled={!action.completionOutcome} onClick={submitForVerification}>Submit for verification</button>
          </section> : null}

          {action.status === "awaiting_verification" ? <section className="mc-actionVerification">
            <div className="mc-actionWorkspaceSectionHead"><div><span>Independent operational review</span><h3>Verify outcome</h3></div></div>
            <p>Reported outcome: <strong>{action.completionOutcome || "Not recorded"}</strong></p>
            {canReview ? <>
              <textarea value={action.verificationNote || ""} onChange={(event) => commit({ ...action, verificationNote: event.target.value }, "Verification note saved")} placeholder="Explain the verification decision..." />
              <div><button type="button" onClick={() => verifyAction("accept")}>Verify</button><button type="button" disabled={!String(action.verificationNote || "").trim()} onClick={() => verifyAction("more")}>Needs more work</button><button type="button" disabled={!String(action.verificationNote || "").trim()} onClick={() => verifyAction("reject")}>Reject</button></div>
              <small>{String(action.verificationNote || "").trim() ? `MatrixOS will email ${action.startedBy} when the decision is recorded.` : "A reason is required for Needs More Work or Reject."}</small>
            </> : <div className="mc-actionReviewNotice"><strong>Awaiting another Owner or Administrator</strong><p>The person who performed the action cannot independently verify their own work. A different authorised reviewer must open this record from Executive Actions.</p></div>}
          </section> : null}

          {action.status === "needs_more_work" || action.status === "rejected" ? <section className="mc-actionCorrection"><span>{action.status === "rejected" ? "Verification rejected" : "Correction requested"}</span><h3>{action.status === "rejected" ? "Action rejected" : "More work required"}</h3><p>{action.verificationNote || "Review the verifier feedback, continue the outstanding work and submit again."}</p><small>Reviewed by {action.reviewedBy || "Owner / Admin"}{action.reviewedAt ? ` · ${new Date(action.reviewedAt).toLocaleString()}` : ""}</small><button type="button" onClick={() => commit({ ...action, status: "in_progress", verificationNote: "" }, "Action reopened")}>Reopen execution</button></section> : null}
          {action.status === "verified" ? <section className="mc-actionVerified"><span>Verified outcome</span><h3>Action accepted</h3><p>{action.completionSummary || "The operational outcome was verified."}</p><small>Verified by {action.verifiedBy} · {new Date(action.verifiedAt).toLocaleString()}</small></section> : null}
        </aside>
      </div>
      </div>

      {evidencePreview ? <div className="mc-evidencePreviewBackdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setEvidencePreview(null); }}>
        <section className="mc-evidencePreviewModal" role="dialog" aria-modal="true" aria-label={`Evidence: ${evidencePreview.name}`}>
          <header><div><span>Operational evidence</span><h3>{evidencePreview.name}</h3><small>{Math.max(1, Math.round((evidencePreview.size || 0) / 1024))} KB · Added by {evidencePreview.addedBy}</small></div></header>
          <div className="mc-evidencePreviewBody">
            {evidencePreview.dataUrl && String(evidencePreview.type || "").startsWith("image/") ? <img src={evidencePreview.dataUrl} alt={evidencePreview.name} /> : null}
            {evidencePreview.dataUrl && String(evidencePreview.type || "").includes("pdf") ? <iframe title={evidencePreview.name} src={evidencePreview.dataUrl} /> : null}
            {!evidencePreview.dataUrl ? <div className="mc-evidencePreviewUnavailable"><strong>Preview not stored</strong><p>This evidence was saved before preview support, or the file exceeded the 5 MB preview limit. Its audit metadata remains available.</p></div> : null}
            {evidencePreview.dataUrl && !String(evidencePreview.type || "").startsWith("image/") && !String(evidencePreview.type || "").includes("pdf") ? <div className="mc-evidencePreviewUnavailable"><strong>Preview unavailable for this file type</strong><p>You can still download the stored evidence file.</p></div> : null}
          </div>
          <footer>{evidencePreview.dataUrl ? <a href={evidencePreview.dataUrl} download={evidencePreview.name}>Download evidence</a> : null}<button type="button" onClick={() => setEvidencePreview(null)}>Close</button></footer>
        </section>
      </div> : null}

      <footer className="mc-actionWorkspaceFooter"><div><span>{action.status === "verified" ? "Action verified" : action.status === "awaiting_verification" ? "Awaiting verification" : action.status === "needs_more_work" ? "Correction requested" : action.status === "rejected" ? "Action rejected" : "Execution in progress"}</span><small>{progress < 100 ? `${tasks.length - completeCount} tasks remaining` : action.status === "in_progress" ? "Record the outcome and submit" : statusLabel}</small></div><div><button type="button" className="is-secondary" onClick={closeWorkspace}>Close</button><span className={`mc-actionSaveState ${savedMessage ? "is-visible" : ""}`} aria-live="polite">{savedMessage ? `✓ ${savedMessage}` : ""}</span>{progress === 100 && action.status === "in_progress" ? <button type="button" className="is-primary" onClick={() => setCompletionOpen(!completionOpen)}>Complete action</button> : null}</div></footer>
    </section>
  </div>;
}

function PeopleIntelligenceWorkspace({ profiles }) {
  const [events, setEvents] = useState(() => buildOperationalActivity());
  const [sites, setSites] = useState(() => loadSites());
  const [peopleQuery, setPeopleQuery] = useState("");
  const [healthPage, setHealthPage] = useState(0);
  const [workloadPage, setWorkloadPage] = useState(0);
  const [riskFilter, setRiskFilter] = useState("all");
  const [riskPage, setRiskPage] = useState(0);
  const [expandedRiskId, setExpandedRiskId] = useState("");
  const [workspaceAction, setWorkspaceAction] = useState(null);
  const [actionRevision, setActionRevision] = useState(0);
  const [executionFilter, setExecutionFilter] = useState("all");
  const [intelligenceView, setIntelligenceView] = useState(() => {
    const allowed = new Set(["overview", "operational", "alerts", "actions", "executions", "risk", "forecast"]);
    try {
      const hash = String(window.location.hash || "").toLowerCase();
      if (hash === "#executive-actions" || hash === "#executions") return "executions";
      const stored = window.sessionStorage.getItem("mc-intelligence-view") || "overview";
      return allowed.has(stored) ? stored : "overview";
    } catch { return "overview"; }
  });
  const PEOPLE_PAGE_SIZE = 6;
  const WORKLOAD_PAGE_SIZE = 6;
  const RISK_PAGE_SIZE = 5;

  useEffect(() => {
    const openFromHash = () => {
      const hash = String(window.location.hash || "").toLowerCase();
      if (hash === "#executive-actions" || hash === "#executions") {
        setIntelligenceView("executions");
        try { window.sessionStorage.setItem("mc-intelligence-view", "executions"); } catch {}
      }
    };
    openFromHash();
    window.addEventListener("hashchange", openFromHash);
    return () => window.removeEventListener("hashchange", openFromHash);
  }, []);

  useEffect(() => {
    const refresh = () => {
      setEvents(buildOperationalActivity());
      setSites(loadSites());
    };
    window.addEventListener(MC_RESPONSE_EVENT, refresh);
    window.addEventListener(MC_ACTIVITY_EVENT, refresh);
    window.addEventListener(MC_SITE_EVENT, refresh);
    window.addEventListener("mc-session-records-updated", refresh);
    const refreshActions = () => setActionRevision((value) => value + 1);
    window.addEventListener("mc-executive-actions-updated", refreshActions);
    window.addEventListener("mc-cloud-sync", refresh);
    return () => {
      window.removeEventListener(MC_RESPONSE_EVENT, refresh);
      window.removeEventListener(MC_ACTIVITY_EVENT, refresh);
      window.removeEventListener(MC_SITE_EVENT, refresh);
      window.removeEventListener("mc-session-records-updated", refresh);
      window.removeEventListener("mc-executive-actions-updated", refreshActions);
      window.removeEventListener("mc-cloud-sync", refresh);
    };
  }, []);

  const intelligence = useMemo(() => {
    const now = Date.now();
    const cutoff = now - 30 * 86400000;
    const recent = events.filter((event) => new Date(event.timestamp).getTime() >= cutoff);
    const responseEvents = recent.filter((event) => String(event.category).toLowerCase() === "response");
    const alertsManaged = responseEvents.filter((event) => /response created|critical response|medium response|high response|low response/i.test(`${event.action} ${event.title}`)).length;
    const escalations = responseEvents.filter((event) => /escalat/i.test(`${event.action} ${event.title}`)).length;
    const resolved = responseEvents.filter((event) => /resolv|verified|closed/i.test(`${event.action} ${event.title}`)).length;
    const activeResponders = new Set(recent.filter((event) => event.person && event.person !== "System").map((event) => event.person)).size;

    const grouped = new Map();
    responseEvents.forEach((event) => {
      const responseId = event.metadata?.responseId;
      if (!responseId) return;
      const list = grouped.get(responseId) || [];
      list.push(event);
      grouped.set(responseId, list);
    });
    const responseMinutes = [];
    grouped.forEach((items) => {
      const ordered = items.slice().sort((a,b)=>new Date(a.timestamp)-new Date(b.timestamp));
      const start = ordered.find((item)=>/created/i.test(`${item.action} ${item.title}`));
      const acted = ordered.find((item)=>/acknowledg|opened|correct|resolv|verified|viewed|clicked/i.test(`${item.action} ${item.title}`));
      if (!start || !acted) return;
      const minutes = Math.max(0, Math.round((new Date(acted.timestamp)-new Date(start.timestamp))/60000));
      if (Number.isFinite(minutes)) responseMinutes.push(minutes);
    });
    const avgResponse = responseMinutes.length ? Math.round(responseMinutes.reduce((a,b)=>a+b,0)/responseMinutes.length) : null;
    const escalationRate = alertsManaged ? Math.round((escalations / alertsManaged) * 100) : 0;
    const resolutionRate = alertsManaged ? Math.min(100, Math.round((resolved / alertsManaged) * 100)) : 100;
    const speedScore = avgResponse == null ? 100 : Math.max(0, 100 - Math.min(70, avgResponse));
    const responseScore = Math.round((resolutionRate * .55) + ((100-escalationRate) * .25) + (speedScore * .20));

    const visiblePeople = profiles.filter((profile) => !isHiddenSystemIdentity(profile));
    const activeSites = sites.filter((site) => site.active !== false);
    const personById = new Map(profiles.map((profile) => [profile.id, profile]));
    const assignments = new Map();
    visiblePeople.forEach((profile) => assignments.set(profile.id, { siteIds: new Set(), roles: new Set() }));
    activeSites.forEach((site) => {
      [
        ["siteManagerPersonId", "Site Manager"],
        ["operationsManagerPersonId", "Operations Manager"],
        ["ownerPersonId", "Owner"],
      ].forEach(([key, role]) => {
        const id = site?.[key];
        if (!id || !personById.has(id)) return;
        const current = assignments.get(id) || { siteIds: new Set(), roles: new Set() };
        current.siteIds.add(site.siteId || site.id || site.name);
        current.roles.add(role);
        assignments.set(id, current);
      });
    });

    const peopleHealth = visiblePeople.map((profile) => {
      const name = fullName(profile);
      const personEvents = recent.filter((event) => String(event.person || "").trim().toLowerCase() === name.toLowerCase());
      const personResponses = personEvents.filter((event) => String(event.category).toLowerCase() === "response");
      const personEscalations = personResponses.filter((event) => /escalat/i.test(`${event.action} ${event.title}`)).length;
      const personResolved = personResponses.filter((event) => /resolv|verified|closed/i.test(`${event.action} ${event.title}`)).length;
      const assignment = assignments.get(profile.id) || { siteIds: new Set(), roles: new Set() };
      const siteCount = assignment.siteIds.size;
      const responseCount = personResponses.length;
      const activityCount = personEvents.length;
      const responseHealth = responseCount ? Math.min(100, Math.round((personResolved / Math.max(1, responseCount)) * 100)) : 70;
      const reliability = Math.max(35, 100 - personEscalations * 12);
      const activitySignal = Math.min(100, 55 + activityCount * 3);
      const coverageSignal = siteCount ? Math.min(100, 70 + siteCount * 5) : 48;
      const score = Math.max(0, Math.min(100, Math.round(responseHealth * .35 + reliability * .25 + activitySignal * .22 + coverageSignal * .18)));
      const tone = score >= 90 ? "healthy" : score >= 75 ? "watch" : score >= 55 ? "busy" : "critical";
      const label = score >= 90 ? "Excellent" : score >= 75 ? "Stable" : score >= 55 ? "Watch" : "Critical";
      const load = siteCount * 4 + responseCount * 2 + activityCount + personEscalations * 5;
      return { profile, name, score, tone, label, siteCount, responseCount, personEscalations, activityCount, load, roles: Array.from(assignment.roles) };
    }).sort((a,b) => b.score - a.score || b.load - a.load);

    const maxLoad = Math.max(1, ...peopleHealth.map((item) => item.load));
    const workload = peopleHealth.slice().sort((a,b)=>b.load-a.load).map((item) => ({...item, loadPercent: Math.max(5, Math.round((item.load/maxLoad)*100))}));

    // Coverage must count only current, valid responsibility assignments.
    // A stale/legacy ID can remain in storage while Sites renders an empty field;
    // it must not make Intelligence report that the site is covered.
    const validAssignedPersonId = (site, key) => {
      const id = String(site?.[key] || "").trim();
      return id && personById.has(id) ? id : "";
    };
    const siteManagerCovered = activeSites.filter((site) => Boolean(validAssignedPersonId(site, "siteManagerPersonId"))).length;
    const operationsCovered = activeSites.filter((site) => Boolean(validAssignedPersonId(site, "operationsManagerPersonId"))).length;
    const ownerCovered = activeSites.filter((site) => Boolean(validAssignedPersonId(site, "ownerPersonId"))).length;
    const peopleWithoutSites = peopleHealth.filter((item) => item.siteCount === 0).length;

    // A site can only have one current Owner in the Sites workspace. This check
    // protects against older/legacy owner fields that may still contain a second,
    // different person and would make the ownership source ambiguous.
    const duplicateOwners = activeSites.filter((site) => {
      const ownerIds = [
        site?.ownerPersonId,
        site?.ownerId,
        site?.responseOwnerPersonId,
        site?.responseOwnerId,
      ]
        .map((value) => String(value || "").trim())
        .filter((id) => id && personById.has(id));
      return new Set(ownerIds).size > 1;
    }).length;

    // Escalation policy owns timing and channels; Directory owns contact details.
    // Count an escalation route as missing only when a currently assigned role
    // cannot be reached through any of the channels enabled for that role.
    const rolePolicy = loadRoleEscalationPolicy();
    const missingEscalation = activeSites.reduce((count, site) => {
      return count + ESCALATION_ROLES.reduce((roleCount, role) => {
        const personId = validAssignedPersonId(site, role.assignmentKey);
        if (!personId) return roleCount;
        const person = personById.get(personId);
        const channels = rolePolicy?.[role.key]?.channels || [];
        const hasReachableChannel = channels.some((channel) => {
          if (channel === "email") return Boolean(String(person?.email || "").trim());
          if (channel === "whatsapp") return Boolean(String(person?.whatsapp || person?.phone || "").trim());
          return false;
        });
        return roleCount + (hasReachableChannel ? 0 : 1);
      }, 0);
    }, 0);
    // Phase 6H.5B.4 — Escalation Intelligence. Reconstruct each response journey
    // from the shared Activity / Response event source rather than storing a second
    // analytics record. Sites owns people, Escalation owns policy, Activity owns facts.
    const responseJourneys = [];
    grouped.forEach((items, responseId) => {
      const ordered = items.slice().sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const combined = ordered.map((item) => `${item.action || ""} ${item.title || ""} ${item.description || ""} ${item.role || ""} ${JSON.stringify(item.metadata || {})}`).join(" ").toLowerCase();
      const created = ordered.some((item) => /response created|critical response|high response|medium response|low response/i.test(`${item.action} ${item.title}`));
      if (!created) return;
      const reachedOperations = /operations manager|operations escalation|escalat(?:ed|ion).*operations|operations.*notified/.test(combined);
      const reachedOwner = /owner|owner escalation|escalat(?:ed|ion).*owner|owner.*notified/.test(combined);
      const isResolved = /resolv|verified|closed/.test(combined);
      const isAcknowledged = /acknowledg|opened|viewed|clicked|correct/.test(combined);
      let resolutionStage = "open";
      if (isResolved) resolutionStage = reachedOwner ? "owner" : reachedOperations ? "operations" : "siteManager";
      responseJourneys.push({ responseId, reachedOperations, reachedOwner, isResolved, isAcknowledged, resolutionStage });
    });
    const journeyTotal = responseJourneys.length || alertsManaged;
    const reachedOperationsCount = responseJourneys.filter((item) => item.reachedOperations).length;
    const reachedOwnerCount = responseJourneys.filter((item) => item.reachedOwner).length;
    const resolvedStageOne = responseJourneys.filter((item) => item.resolutionStage === "siteManager").length;
    const resolvedAtOperations = responseJourneys.filter((item) => item.resolutionStage === "operations").length;
    const resolvedAtOwner = responseJourneys.filter((item) => item.resolutionStage === "owner").length;
    const unresolvedJourneys = Math.max(0, journeyTotal - responseJourneys.filter((item) => item.isResolved).length);
    const stoppedBeforeEscalation = Math.max(resolvedStageOne, journeyTotal - reachedOperationsCount - unresolvedJourneys);
    const escalationFlow = {
      total: journeyTotal,
      stageOne: Math.max(0, stoppedBeforeEscalation),
      operations: reachedOperationsCount,
      owner: reachedOwnerCount,
      unresolved: unresolvedJourneys,
      resolvedAtOperations,
      resolvedAtOwner,
    };

    const loadBands = peopleHealth.reduce((result, item) => {
      const ratio = item.load / maxLoad;
      const key = ratio >= .85 ? "Critical" : ratio >= .6 ? "High" : ratio >= .3 ? "Medium" : "Low";
      result[key] += 1;
      return result;
    }, { Low:0, Medium:0, High:0, Critical:0 });

    // 6H.5C.1 — deterministic Operational Risk Radar.
    // This creates no new source of truth. Every score is calculated from Sites,
    // Directory, Escalation policy, Activity and Response Designed facts.
    const healthByPersonId = new Map(peopleHealth.map((item) => [item.profile.id, item]));
    const responseGroupsForSite = Array.from(grouped.entries());
    const siteRisk = activeSites.map((site) => {
      const siteId = String(site.siteId || site.id || site.name || "");
      const siteName = String(site.name || site.siteName || site.label || formatStoreLabel(siteId) || "Site");
      const siteTokens = [siteId, siteName, site.csvKey, site.inventoryKey].filter(Boolean).map((value) => String(value).toLowerCase());
      const belongsToSite = (event) => {
        const haystack = `${event.site || ""} ${event.description || ""} ${event.title || ""} ${JSON.stringify(event.metadata || {})}`.toLowerCase();
        return siteTokens.some((token) => token && (haystack.includes(token) || token.includes(haystack.trim())));
      };
      const siteEvents = recent.filter(belongsToSite);
      const siteResponses = responseEvents.filter(belongsToSite);
      const assignedIds = ESCALATION_ROLES.map((role) => validAssignedPersonId(site, role.assignmentKey)).filter(Boolean);
      const missingRoles = Math.max(0, 3 - assignedIds.length);
      const coverageRisk = Math.round((missingRoles / 3) * 100);

      const siteResponseMinutes = [];
      let unresolvedCritical = 0;
      responseGroupsForSite.forEach(([, items]) => {
        const siteItems = items.filter(belongsToSite);
        if (!siteItems.length) return;
        const ordered = siteItems.slice().sort((a,b)=>new Date(a.timestamp)-new Date(b.timestamp));
        const combined = ordered.map((item)=>`${item.action || ""} ${item.title || ""} ${item.description || ""}`).join(" ").toLowerCase();
        const start = ordered.find((item)=>/created|critical response|high response|medium response|low response/i.test(`${item.action} ${item.title}`));
        const acted = ordered.find((item)=>/acknowledg|opened|correct|resolv|verified|viewed|clicked/i.test(`${item.action} ${item.title}`));
        if (start && acted) {
          const mins = Math.max(0, Math.round((new Date(acted.timestamp)-new Date(start.timestamp))/60000));
          if (Number.isFinite(mins)) siteResponseMinutes.push(mins);
        }
        if (/critical/.test(combined) && !/resolv|verified|closed/.test(combined)) unresolvedCritical += 1;
      });
      const siteAvgResponse = siteResponseMinutes.length ? Math.round(siteResponseMinutes.reduce((a,b)=>a+b,0)/siteResponseMinutes.length) : null;
      const responseRisk = siteAvgResponse == null ? (siteResponses.length ? 72 : 42) : Math.min(100, Math.round((siteAvgResponse / 120) * 100));
      const criticalRisk = Math.min(100, unresolvedCritical * 25);
      const assignedLoads = assignedIds.map((id)=>healthByPersonId.get(id)?.load || 0);
      const workloadRisk = assignedLoads.length ? Math.min(100, Math.round((Math.max(...assignedLoads) / maxLoad) * 100)) : 70;
      const activityCount = siteEvents.length;
      const activityRisk = activityCount === 0 ? 100 : activityCount < 3 ? 75 : activityCount < 8 ? 45 : 15;
      const score = Math.max(0, Math.min(100, Math.round(coverageRisk*.30 + responseRisk*.25 + criticalRisk*.20 + workloadRisk*.15 + activityRisk*.10)));
      const tone = score >= 81 ? "high" : score >= 61 ? "medium" : score >= 31 ? "watch" : "healthy";
      const contributors = [
        { key:"coverage", label:"Coverage", raw:coverageRisk, weighted:Math.round(coverageRisk*.30) },
        { key:"response", label:"Response delay", raw:responseRisk, weighted:Math.round(responseRisk*.25) },
        { key:"critical", label:"Open critical", raw:criticalRisk, weighted:Math.round(criticalRisk*.20) },
        { key:"people", label:"People workload", raw:workloadRisk, weighted:Math.round(workloadRisk*.15) },
        { key:"activity", label:"Recent activity", raw:activityRisk, weighted:Math.round(activityRisk*.10) },
      ].sort((a,b)=>b.weighted-a.weighted);
      const top = contributors[0];
      const recommendation = top.key === "coverage"
        ? `Assign the ${!validAssignedPersonId(site,"siteManagerPersonId") ? "Site Manager" : !validAssignedPersonId(site,"operationsManagerPersonId") ? "Operations Manager" : "Owner"}`
        : top.key === "response" ? "Review the response path and first-response delay"
        : top.key === "critical" ? `Resolve ${unresolvedCritical || "open"} critical response${unresolvedCritical === 1 ? "" : "s"}`
        : top.key === "people" ? "Redistribute responsibility from the busiest responder"
        : "Confirm the site is active and counting consistently";
      const estimatedScore = Math.max(0, score - top.weighted);
      const recentSignal = Math.min(18, Math.round((activityRisk + responseRisk) / 12));
      const projection7 = Math.max(0, Math.min(100, score + (score >= 31 ? recentSignal : Math.round(recentSignal / 2))));
      const projection30 = Math.max(projection7, Math.min(100, projection7 + Math.round((coverageRisk + criticalRisk + activityRisk) / 18)));
      const evidenceCount = Math.max(1, siteEvents.length + siteResponses.length);
      const confidence = Math.max(52, Math.min(97, Math.round(56 + Math.min(22, evidenceCount * 1.6) + (siteResponseMinutes.length ? 12 : 0) + (assignedIds.length ? 5 : 0))));
      const trend = projection7 > score + 4 ? "rising" : projection7 < score - 4 ? "improving" : "stable";
      const daysToHigh = score >= 81 ? 0 : projection30 <= score ? null : Math.max(3, Math.round((81 - score) / Math.max(1, (projection30 - score) / 30)));
      const reasonText = top.key === "coverage" ? "missing response ownership" : top.key === "response" ? "slower first-response behaviour" : top.key === "critical" ? "open critical responses" : top.key === "people" ? "concentrated responder workload" : "declining operational activity";
      const predictionSentence = score >= 81
        ? `${siteName} is already operating at High Risk, driven mainly by ${reasonText}.`
        : daysToHigh && daysToHigh <= 30
          ? `${siteName} is likely to enter High Risk within approximately ${daysToHigh} day${daysToHigh === 1 ? "" : "s"} if no operational changes are made.`
          : `${siteName} is expected to remain ${tone === "healthy" ? "within a healthy range" : "under operational pressure"} during the next 30 days unless the main risk contributor changes.`;
      const actionCatalog = {
        coverage: [
          { label: recommendation, points: Math.max(8, top.weighted) },
          { label: "Complete the remaining response-role assignments", points: Math.max(5, Math.round(coverageRisk * .12)) },
        ],
        response: [
          { label: "Reduce the first-response delay", points: Math.max(8, top.weighted) },
          { label: "Enable a faster response channel", points: 6 },
        ],
        critical: [
          { label: `Resolve ${unresolvedCritical || "the"} open critical response${unresolvedCritical === 1 ? "" : "s"}`, points: Math.max(8, top.weighted) },
          { label: "Verify the latest corrective count", points: 5 },
        ],
        people: [
          { label: "Redistribute responsibility from the busiest responder", points: Math.max(7, top.weighted) },
          { label: "Assign a backup responder", points: 6 },
        ],
        activity: [
          { label: "Confirm the site is active and counting consistently", points: Math.max(6, top.weighted) },
          { label: "Schedule the next verified count", points: 5 },
        ],
      };
      const secondaryActions = contributors.slice(1).map((item) => ({
        label: item.key === "coverage" ? "Complete response-role coverage" : item.key === "response" ? "Improve response speed" : item.key === "critical" ? "Close critical responses" : item.key === "people" ? "Balance responder workload" : "Increase verified site activity",
        points: Math.max(3, Math.round(item.weighted * .7)),
      }));
      const actions = [...(actionCatalog[top.key] || []), ...secondaryActions]
        .filter((item, index, list) => item.points > 0 && list.findIndex((candidate) => candidate.label === item.label) === index)
        .sort((a,b) => b.points - a.points)
        .slice(0,4);
      let runningRisk = score;
      const simulation = actions.map((action) => {
        runningRisk = Math.max(0, runningRisk - action.points);
        return { ...action, projectedRisk: runningRisk };
      });
      const ignoredEscalationProbability = Math.min(98, Math.max(8, Math.round(projection7 * .86 + unresolvedCritical * 4)));
      const expectedDelayMinutes = siteAvgResponse == null ? Math.round(60 + projection7 * 2.1) : Math.max(siteAvgResponse, Math.round(siteAvgResponse * (1 + projection7 / 180)));
      return {
        id:siteId || siteName, name:siteName, score, tone, contributors, recommendation,
        estimatedScore, improvement:Math.max(0,score-estimatedScore), activityCount,
        unresolvedCritical, siteAvgResponse, missingRoles, assignedPeople:assignedIds.length,
        projection7, projection30, confidence, trend, daysToHigh, predictionSentence,
        evidence: { events: siteEvents.length, responses: siteResponses.length, escalations: siteResponses.filter((event)=>/escalat/i.test(`${event.action || ""} ${event.title || ""}`)).length },
        actions, simulation, ignoredEscalationProbability, expectedDelayMinutes,
      };
    }).sort((a,b)=>b.score-a.score || a.name.localeCompare(b.name));
    const riskSummary = siteRisk.reduce((acc,item)=>{acc[item.tone]=(acc[item.tone]||0)+1;return acc;},{high:0,medium:0,watch:0,healthy:0});

    return {
      activeResponders, alertsManaged, avgResponse, escalationRate, responseScore, resolutionRate,
      peopleHealth, workload, activeSiteCount: activeSites.length,
      coverage: { siteManagerCovered, operationsCovered, ownerCovered, peopleWithoutSites, duplicateOwners, missingEscalation },
      escalationFlow,
      loadBands,
      siteRisk,
      riskSummary,
    };
  }, [events, profiles, sites]);

  const scoreTone = intelligence.responseScore >= 90 ? "healthy" : intelligence.responseScore >= 75 ? "watch" : "critical";
  const avgLabel = intelligence.avgResponse == null ? "—" : `${intelligence.avgResponse} min`;
  const totalSites = intelligence.activeSiteCount;
  const coverageItems = [
    ["Site Managers", intelligence.coverage.siteManagerCovered, totalSites],
    ["Operations Managers", intelligence.coverage.operationsCovered, totalSites],
    ["Owners", intelligence.coverage.ownerCovered, totalSites],
  ];
  const overallHealthLabel = intelligence.responseScore >= 90 ? "Excellent" : intelligence.responseScore >= 75 ? "Stable" : intelligence.responseScore >= 55 ? "Watch" : "Critical";
  const normalizedPeopleQuery = peopleQuery.trim().toLowerCase();
  const filteredPeopleHealth = intelligence.peopleHealth.filter((item) => !normalizedPeopleQuery || `${item.name} ${item.roles.join(" ")} ${profileOperationalRole(item.profile)}`.toLowerCase().includes(normalizedPeopleQuery));
  const healthPageCount = Math.max(1, Math.ceil(filteredPeopleHealth.length / PEOPLE_PAGE_SIZE));
  const safeHealthPage = Math.min(healthPage, healthPageCount - 1);
  const visiblePeopleHealth = filteredPeopleHealth.slice(safeHealthPage * PEOPLE_PAGE_SIZE, safeHealthPage * PEOPLE_PAGE_SIZE + PEOPLE_PAGE_SIZE);
  const filteredWorkload = intelligence.workload.filter((item) => !normalizedPeopleQuery || `${item.name} ${item.roles.join(" ")} ${profileOperationalRole(item.profile)}`.toLowerCase().includes(normalizedPeopleQuery));
  const workloadPageCount = Math.max(1, Math.ceil(filteredWorkload.length / WORKLOAD_PAGE_SIZE));
  const safeWorkloadPage = Math.min(workloadPage, workloadPageCount - 1);
  const visibleWorkload = filteredWorkload.slice(safeWorkloadPage * WORKLOAD_PAGE_SIZE, safeWorkloadPage * WORKLOAD_PAGE_SIZE + WORKLOAD_PAGE_SIZE);
  const filteredRisks = intelligence.siteRisk.filter((item) => riskFilter === "all" || item.tone === riskFilter || item.contributors[0]?.key === riskFilter);
  const riskPageCount = Math.max(1, Math.ceil(filteredRisks.length / RISK_PAGE_SIZE));
  const safeRiskPage = Math.min(riskPage, riskPageCount - 1);
  const visibleRisks = filteredRisks.slice(safeRiskPage * RISK_PAGE_SIZE, safeRiskPage * RISK_PAGE_SIZE + RISK_PAGE_SIZE);

  // 6H.5C.3 — Executive Prioritisation Engine. Rank leadership work by
  // risk, operational impact and the opportunity created by the best action.
  const executivePriorities = intelligence.siteRisk.map((risk) => {
    const bestAction = risk.actions?.[0] || { label: risk.recommendation, points: risk.improvement || 0 };
    const opportunity = Math.max(0, Math.min(100, Math.round((bestAction.points || 0) * 3 + (100 - risk.estimatedScore) * .2)));
    const businessImpact = Math.min(100, Math.round(
      risk.score * .38 +
      Math.min(100, risk.unresolvedCritical * 22) * .24 +
      Math.min(100, (risk.expectedDelayMinutes || 0) / 2) * .18 +
      Math.min(100, risk.missingRoles * 34) * .20
    ));
    const priorityScore = Math.min(100, Math.round(
      risk.score * .35 + businessImpact * .25 + opportunity * .20 +
      Math.min(100, risk.unresolvedCritical * 25) * .10 +
      Math.min(100, (risk.expectedDelayMinutes || 0) / 2) * .10
    ));
    const urgency = priorityScore >= 80 ? 'Immediate' : priorityScore >= 60 ? 'Today' : priorityScore >= 40 ? 'Watch' : 'Protected';
    const effortMinutes = Math.max(3, Math.min(25, 3 + Math.round((risk.missingRoles + (risk.actions?.length || 1)) * 2.5)));
    const opportunityStars = Math.max(1, Math.min(5, Math.ceil(opportunity / 20)));
    return { ...risk, bestAction, opportunity, businessImpact, priorityScore, urgency, effortMinutes, opportunityStars };
  }).sort((a,b) => b.priorityScore - a.priorityScore || b.score - a.score).slice(0,5);
  const executiveFocus = executivePriorities[0] || null;
  const currentOrganisationHealth = Math.max(0, Math.min(100, 100 - Math.round((intelligence.siteRisk.reduce((sum,item)=>sum+item.score,0) / Math.max(1,intelligence.siteRisk.length)))));
  const recommendedRiskReduction = executivePriorities.reduce((sum,item)=>sum + Math.max(0,item.score - (item.simulation?.[0]?.projectedRisk ?? item.estimatedScore)),0);
  const recommendedHealth = Math.min(100, currentOrganisationHealth + Math.round(recommendedRiskReduction / Math.max(1,intelligence.siteRisk.length)));
  const forecast7Health = Math.min(100, recommendedHealth + Math.round((100 - recommendedHealth) * .18));
  const forecast30Health = Math.min(100, forecast7Health + Math.round((100 - forecast7Health) * .24));
  const executiveFeed = events
    .filter((event) => new Date(event.timestamp).getTime() >= Date.now() - 7 * 86400000)
    .filter((event) => /risk|manager assigned|owner assigned|operations manager assigned|resolved|escalat|coverage|permission/i.test(`${event.action || ''} ${event.title || ''} ${event.description || ''}`))
    .slice(0,5);

  // 6H.5D.1 — Executive Action Centre.
  // Recommendations remain derived from the same intelligence model; this layer only
  // groups them into executable leadership work and never creates a second source of truth.
  const executiveActionMap = new Map();
  executivePriorities.forEach((item) => {
    const label = item.bestAction?.label || "Review operational risk";
    const key = label.toLowerCase();
    const reduction = Math.max(0, item.score - (item.simulation?.[0]?.projectedRisk ?? item.estimatedScore));
    const current = executiveActionMap.get(key) || {
      id:key.replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,""),
      label,
      sites:[],
      totalReduction:0,
      effortMinutes:0,
      confidence:0,
      priority:0,
    };
    current.sites.push({ id:item.id, name:item.name, risk:item.score, projected:item.simulation?.[0]?.projectedRisk ?? item.estimatedScore });
    current.totalReduction += reduction;
    current.effortMinutes += item.effortMinutes;
    current.confidence = Math.max(current.confidence, item.confidence || 0);
    current.priority = Math.max(current.priority, item.priorityScore || 0);
    executiveActionMap.set(key,current);
  });
  const executiveActions = Array.from(executiveActionMap.values())
    .map((action) => ({
      ...action,
      averageReduction: Math.round(action.totalReduction / Math.max(1, action.sites.length)),
      organisationGain: Math.max(1, Math.round(action.totalReduction / Math.max(1, intelligence.siteRisk.length))),
      opportunity: action.totalReduction >= 60 ? "Highest impact" : action.totalReduction >= 30 ? "High impact" : "Focused improvement",
      route: /critical|response|correct|verify/i.test(action.label) ? "/morpheus-count/workspace#notifications" : /escalat|channel/i.test(action.label) ? "/morpheus-count/profiles#escalation" : "/morpheus-count/sites",
    }))
    .sort((a,b)=>b.totalReduction-a.totalReduction || b.priority-a.priority)
    .slice(0,5);
  const actionCentreGain = executiveActions.reduce((sum,action)=>sum+action.organisationGain,0);
  const actionCentreEffort = executiveActions.reduce((sum,action)=>sum+action.effortMinutes,0);
  const intelligenceViews = [
    { key: "overview", label: "Executive Summary", description: "What needs attention now" },
    { key: "operational", label: "Operational Health", description: "People, workload and coverage" },
    { key: "alerts", label: "Alert Flow", description: "Response journey and bottlenecks" },
    { key: "actions", label: "Action Centre", description: "Highest-value work queue" },
    { key: "executions", label: "Executive Actions", description: "Resume and review execution" },
    { key: "risk", label: "Risk Radar", description: "Predictive site intelligence" },
    { key: "forecast", label: "Forecast", description: "Priorities, feed and outlook" },
  ];
  const savedExecutiveActions = useMemo(() => listExecutiveActionWorkspaces(), [actionRevision, workspaceAction?.updatedAt, workspaceAction?.status]);
  const executiveActionCounts = savedExecutiveActions.reduce((result, item) => {
    const key = item.status || "in_progress";
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});
  const executionFilters = [
    { key: "all", label: "All", count: savedExecutiveActions.length },
    { key: "in_progress", label: "In Progress", count: executiveActionCounts.in_progress || 0 },
    { key: "awaiting_verification", label: "Awaiting Verification", count: executiveActionCounts.awaiting_verification || 0 },
    { key: "verified", label: "Verified", count: executiveActionCounts.verified || 0 },
    { key: "needs_more_work", label: "Needs More Work", count: executiveActionCounts.needs_more_work || 0 },
    { key: "rejected", label: "Rejected", count: executiveActionCounts.rejected || 0 },
    { key: "archived", label: "Archived", count: executiveActionCounts.archived || 0 },
  ];
  const filteredExecutiveActions = executionFilter === "all"
    ? savedExecutiveActions
    : savedExecutiveActions.filter((item) => (item.status || "in_progress") === executionFilter);
  const changeIntelligenceView = (next) => {
    setIntelligenceView(next);
    try { window.sessionStorage.setItem("mc-intelligence-view", next); } catch {}
    if (next === "executions") {
      try { window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}#executive-actions`); } catch {}
    } else if (String(window.location.hash || "").toLowerCase() === "#executive-actions") {
      try { window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`); } catch {}
    }
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
  };

  function startExecutiveAction(action) {
    if (!action) return;
    const stored = loadExecutiveActionWorkspaces()[action.id];
    if (stored) {
      const refreshedSites = (action.sites || []).map((sourceSite) => {
        const existing = (stored.affectedSites || []).find((site) => site.id === sourceSite.id);
        return existing ? { ...sourceSite, ...existing } : {
          ...sourceSite,
          expanded: false,
          tasks: actionTaskTitles(action.label).map((title, index) => ({ id: `${action.id}-${sourceSite.id}-${index + 1}`, title, completed: false, completedBy: null, completedAt: null })),
        };
      });
      const resumed = saveExecutiveActionWorkspace({ ...stored, affectedSites: refreshedSites, updatedAt: new Date().toISOString() });
      setWorkspaceAction(resumed);
      return;
    }
    let actor = null;
    try { actor = getCurrentProfile(); } catch {}
    if (!actor) actor = profiles.find((profile) => profile.isPrimaryOwner || profile.primaryOwner) || profiles[0] || {};
    const workspace = saveExecutiveActionWorkspace(createExecutiveActionWorkspace(action, actor));
    setWorkspaceAction(workspace);
    recordActivity({
      category: "People",
      action: "Executive action started",
      title: action.label,
      description: `Action workspace opened for ${action.sites.length} affected sites.`,
      metadata: { actionId: action.id, affectedSites: action.sites.map((site) => site.name), expectedGain: action.organisationGain },
    });
  }

  return <section className={`mc-intelligenceWorkspace is-view-${intelligenceView}`}>
    {workspaceAction ? <ExecutiveActionWorkspaceModal action={workspaceAction} profiles={profiles} onChange={setWorkspaceAction} onClose={() => setWorkspaceAction(null)} /> : null}
    <nav className="mc-intelligenceViewNav" aria-label="People Intelligence sections">
      {intelligenceViews.map((view) => <button
        key={view.key}
        type="button"
        className={intelligenceView === view.key ? "is-active" : ""}
        onClick={() => changeIntelligenceView(view.key)}
      >
        <strong>{view.label}</strong>
        <small>{view.description}</small>
      </button>)}
    </nav>

    {intelligenceView === "overview" ? (
      <ExecutiveSummary>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Executive Summary Workspace</h3>
          <p>Phase 2 is connected. Only this selected Intelligence workspace is now rendered.</p>
        </div>
      </ExecutiveSummary>
    ) : null}

    {intelligenceView === "operational" ? (
      <OperationalHealth>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Operational Health Workspace</h3>
          <p>People, workload, coverage and response health will be migrated here in Phase 3.</p>
        </div>
      </OperationalHealth>
    ) : null}

    {intelligenceView === "alerts" ? (
      <AlertFlow>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Alert Flow Workspace</h3>
          <p>Response journey and escalation flow content will be migrated here in Phase 3.</p>
        </div>
      </AlertFlow>
    ) : null}

    {intelligenceView === "actions" ? (
      <ActionCentre>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Action Centre Workspace</h3>
          <p>MatrixOS recommendations will be migrated here without execution tracking.</p>
        </div>
      </ActionCentre>
    ) : null}

    {intelligenceView === "executions" ? (
      <ExecutiveActions>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Executive Actions Workspace</h3>
          <p>Leadership execution, review and verification records will be migrated here in Phase 3.</p>
        </div>
      </ExecutiveActions>
    ) : null}

    {intelligenceView === "risk" ? (
      <RiskRadar>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Risk Radar Workspace</h3>
          <p>Predictive site risk and intervention content will be migrated here in Phase 3.</p>
        </div>
      </RiskRadar>
    ) : null}

    {intelligenceView === "forecast" ? (
      <Forecast>
        <div className="mc-intelligencePhasePlaceholder">
          <span>Workspace routing active</span>
          <h3>Forecast Workspace</h3>
          <p>Organisation outlook and verified-execution forecasts will be migrated here in Phase 3.</p>
        </div>
      </Forecast>
    ) : null}
  </section>;
}

function PermissionsPeopleWorkspace({ profiles, updateProfile }) {
  const [selectedId,setSelectedId]=useState(()=>profiles.find((profile)=>!isHiddenSystemIdentity(profile))?.id || "");
  const [draftTabs,setDraftTabs]=useState([]);
  const [savedMessage,setSavedMessage]=useState("");
  useEffect(()=>{ if (!profiles.some((p)=>p.id===selectedId && !isHiddenSystemIdentity(p))) setSelectedId(profiles.find((p)=>!isHiddenSystemIdentity(p))?.id || ""); },[profiles,selectedId]);
  const selected=profiles.find((p)=>p.id===selectedId); 
  const access=selected ? profileAccessLevel(selected) : "standard";
  const owner=access==='owner' || selected?.isPrimaryOwner === true;
  useEffect(()=>{
    if (!selected) return;
    setDraftTabs(profileTabs(selected));
    setSavedMessage("");
  },[selectedId, selected?.tabs, owner]);
  if(!selected)return null;
  const allowed=new Set(owner ? MODULE_PERMISSIONS.map(([key])=>key) : draftTabs);
  const stored=new Set(profileTabs(selected));
  const dirty=!owner && (draftTabs.length!==stored.size || draftTabs.some((key)=>!stored.has(key)));
  function toggle(key){ if(owner)return; setDraftTabs((current)=>current.includes(key)?current.filter((x)=>x!==key):[...current,key]); setSavedMessage(""); }
  function save(){ if(owner)return; const before=profileTabs(selected); updateProfile(selected.id,{tabs:draftTabs}); recordActivity({category:'Access',action:'Permissions changed',title:`Permissions updated · ${fullName(selected)}`,description:'Workspace visibility was changed.',targetId:selected.id,targetName:fullName(selected),metadata:{beforeTabs:before,afterTabs:draftTabs}}); setSavedMessage('Permissions saved. They apply the next time this person signs in.'); }
  function reset(){ setDraftTabs(profileTabs(selected)); setSavedMessage('Changes discarded.'); }
  return <section className="mc-peopleManager"><div className="mc-peopleManager__header"><div><div className="mc-enginePage__eyebrow">Access intelligence</div><h2>Permissions</h2><p>Preview workspace access safely, then save it for the selected person.</p></div><IdentitySelector profiles={profiles} selectedId={selectedId} onChange={setSelectedId}/></div>
  <div className="mc-permissionSummary"><div><span>Editing</span><strong>{fullName(selected)}</strong></div><div><span>Access level</span><strong>{ACCESS_LEVELS.find((x)=>x.value===access)?.label}</strong></div><div><span>Previewed workspaces</span><strong>{allowed.size}</strong></div></div>
  {owner?<div className="mc-peopleNotice"><strong>Primary Owner Protection</strong><br/>This account cannot lose People, Billing, Config or any other workspace. Ownership must be transferred through a controlled workflow first.</div>:<div className="mc-peopleNotice"><strong>Safe editing</strong><br/>You are editing {fullName(selected)}. These changes will not alter your current signed-in session. They apply when this person signs in again.</div>}
  <article className="mc-peopleManager__panel"><div className="mc-permissionMatrix">{MODULE_PERMISSIONS.map(([key,label])=><label key={key} className={allowed.has(key)?'is-enabled':''}><input type="checkbox" checked={allowed.has(key)} disabled={owner} onChange={()=>toggle(key)}/><span><strong>{label}</strong><small>{allowed.has(key)?'Visible in navigation':'Hidden from navigation'}</small></span></label>)}</div>
  <div className="mc-safePermissionActions"><div>{dirty?<span className="mc-unsavedBadge">Unsaved permission changes</span>:<span className="mc-savedBadge">No unsaved changes</span>}{savedMessage?<small>{savedMessage}</small>:null}</div><div><button type="button" className="mc-engineBtn mc-engineBtn--ghost" disabled={!dirty} onClick={reset}>Discard</button><button type="button" className="mc-engineBtn mc-engineBtn--primary" disabled={!dirty || owner} onClick={save}>Save permissions</button></div></div>
  </article></section>;
}

function SitesPeopleWorkspace({ profiles, sections, storeOptions, updateProfile }) {
  const [selectedId, setSelectedId] = useState(() => profiles.find((profile) => !isHiddenSystemIdentity(profile))?.id || "");
  const [draftAllSites, setDraftAllSites] = useState(false);
  const [draftSites, setDraftSites] = useState([]);
  const [draftSections, setDraftSections] = useState([]);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!profiles.some((profile) => profile.id === selectedId && !isHiddenSystemIdentity(profile))) setSelectedId(profiles.find((profile) => !isHiddenSystemIdentity(profile))?.id || "");
  }, [profiles, selectedId]);

  const selected = profiles.find((profile) => profile.id === selectedId);
  const savedAllSites = selected ? (selected.store === "ALL" || getProfileAllowedSiteIds(selected).includes("ALL")) : false;
  const savedSites = selected ? selectedSiteIds(selected) : [];
  const savedSections = selected ? selectedSectionIds(selected) : [];

  useEffect(() => {
    if (!selected) return;
    setDraftAllSites(savedAllSites);
    setDraftSites(savedSites);
    setDraftSections(savedSections);
    setMessage("");
  }, [selectedId, savedAllSites, savedSites.join("|"), savedSections.join("|")]);

  if (!selected) return null;

  const normalizedDraftSites = Array.from(new Set(draftSites.map(normalizeStoreValue).filter(Boolean)));
  const normalizedSavedSites = Array.from(new Set(savedSites.map(normalizeStoreValue).filter(Boolean)));
  const normalizedDraftSections = Array.from(new Set(draftSections.map(normalizeSectionId).filter(Boolean)));
  const normalizedSavedSections = Array.from(new Set(savedSections.map(normalizeSectionId).filter(Boolean)));
  const dirty = draftAllSites !== savedAllSites
    || normalizedDraftSites.join("|") !== normalizedSavedSites.join("|")
    || normalizedDraftSections.join("|") !== normalizedSavedSections.join("|");

  function setAll(flag) {
    setDraftAllSites(flag);
    if (!flag && !normalizedDraftSites.length && storeOptions[0]) setDraftSites([normalizeStoreValue(storeOptions[0])]);
    setMessage("");
  }

  function toggleSite(site) {
    setDraftSites((current) => toggleSiteInList(current, site));
    setMessage("");
  }

  function toggleSection(id) {
    setDraftSections((current) => toggleSectionInList(current, id));
    setMessage("");
  }

  function save() {
    const allowed = draftAllSites ? ["ALL"] : normalizedDraftSites;
    updateProfile(selected.id, {
      store: draftAllSites ? "ALL" : (allowed[0] || ""),
      allowedSiteIds: allowed,
      assignedSectionIds: normalizedDraftSections,
    });
    setMessage("Site and section assignments saved. They apply the next time this person signs in.");
  }

  function discard() {
    setDraftAllSites(savedAllSites);
    setDraftSites(savedSites);
    setDraftSections(savedSections);
    setMessage("Unsaved assignment changes discarded.");
  }

  return <section className="mc-peopleManager"><div className="mc-peopleManager__header"><div><div className="mc-enginePage__eyebrow">Assignment intelligence</div><h2>Sites</h2><p>Control all-sites or selected-site scope, then assign sections from one place.</p></div><IdentitySelector profiles={profiles} selectedId={selectedId} onChange={setSelectedId}/></div>
  <div className="mc-siteScopeSwitch"><button type="button" className={draftAllSites?'is-active':''} onClick={()=>setAll(true)}><strong>All sites</strong><span>Organisation-wide visibility</span></button><button type="button" className={!draftAllSites?'is-active':''} onClick={()=>setAll(false)}><strong>Selected sites</strong><span>Restrict this person to chosen sites</span></button></div>
  <div className="mc-peopleManager__grid"><article className="mc-peopleManager__panel"><h3>Site assignments</h3><p>{draftAllSites?'All sites are currently selected in this draft.':'Choose one or more sites.'}</p><div className="mc-assignmentGrid">{storeOptions.map((site)=><label key={site} className={draftAllSites||normalizedDraftSites.includes(normalizeStoreValue(site))?'is-selected':''}><input type="checkbox" disabled={draftAllSites} checked={draftAllSites||normalizedDraftSites.includes(normalizeStoreValue(site))} onChange={()=>toggleSite(site)}/><span>{formatStoreLabel(site)}</span></label>)}</div></article>
  <article className="mc-peopleManager__panel"><h3>Section assignments</h3><p>Leave all unselected to use the person's site-wide access.</p><div className="mc-assignmentGrid">{sections.map((section)=>{const id=section.sectionId||section.id;return <label key={id} className={normalizedDraftSections.includes(normalizeSectionId(id))?'is-selected':''}><input type="checkbox" checked={normalizedDraftSections.includes(normalizeSectionId(id))} onChange={()=>toggleSection(id)}/><span>{section.name||formatSectionLabel(id)}</span></label>})}</div></article></div>
  <div className="mc-safePermissionActions"><div>{dirty?<span className="mc-unsavedBadge">Unsaved assignment changes</span>:<span className="mc-savedBadge">No unsaved changes</span>}{message?<small>{message}</small>:null}</div><div><button type="button" className="mc-engineBtn mc-engineBtn--ghost" disabled={!dirty} onClick={discard}>Discard</button><button type="button" className="mc-engineBtn mc-engineBtn--primary" disabled={!dirty} onClick={save}>Save assignments</button></div></div>
  </section>;
}

function PeopleScaffold({ tab }) {
  const content = {
    roles: {
      eyebrow: "Operational responsibility",
      title: "Roles",
      body: "Define who owns operational outcomes without confusing responsibility with system access.",
      items: ["Owner", "Operations Manager", "Site Manager", "Supervisor", "Stock Controller", "Auditor", "Custom role"],
    },
    permissions: {
      eyebrow: "Access intelligence",
      title: "Permissions",
      body: "Control which workspaces people can see and which actions they may perform inside each workspace.",
      items: ["Workspace visibility", "Action permissions", "Administrator limits", "Owner-controlled access"],
    },
    sites: {
      eyebrow: "Assignment intelligence",
      title: "Sites",
      body: "Assign one person to one site, selected sites, or all sites from the same People workspace.",
      items: ["Primary site", "Selected sites", "Assigned sections", "All-sites scope"],
    },
    response: {
      eyebrow: "Response designed",
      title: "Response",
      body: "Manage Site Manager, Operations Manager, Owner, verification responsibility and escalation contacts in one place.",
      items: ["Response contacts", "Verification responsibility", "Escalation order", "Email and WhatsApp recipients"],
    },
  }[tab];

  if (!content) return null;

  return (
    <section className="mc-peopleScaffold">
      <article className="mc-peopleScaffold__intro">
        <div className="mc-enginePage__eyebrow">{content.eyebrow}</div>
        <h2>{content.title}</h2>
        <p>{content.body}</p>
      </article>
      <div className="mc-peopleScaffold__grid">
        {content.items.map((item, index) => (
          <article className="mc-peopleScaffold__card" key={item}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{item}</strong>
            <p>Foundation prepared. This capability moves here in the next People Intelligence layer.</p>
          </article>
        ))}
      </div>
    </section>
  );
}


export default function MorpheusCountProfiles() {
  const location = useLocation();
  const navigate = useNavigate();
  const requestedPeopleTab = String(location.hash || "#directory").replace(/^#/, "").toLowerCase();
  const peopleTab = PEOPLE_TABS.some((tab) => tab.key === requestedPeopleTab) ? requestedPeopleTab : "directory";
  const selectPeopleTab = (tabKey) => navigate(`${location.pathname}#${tabKey}`, { replace: true });
  const { user, claims } = useAuth();
  const authEmail = String(user?.email || claims?.email || '').trim().toLowerCase();
  const currentProfile = getCurrentProfile();
  const adminEmail = authEmail || currentProfile?.email || '';
  const adminDisplayName = String(user?.displayName || [currentProfile?.name, currentProfile?.surname].filter(Boolean).join(' ') || '').trim();
  const primaryOwnerEmail = String(mcGetItem('mc-primary-owner-email') || '').trim().toLowerCase();
  const viewerIsPrimaryOwner = currentProfile?.isPrimaryOwner === true || currentProfile?.primaryOwner === true || (primaryOwnerEmail && authEmail === primaryOwnerEmail);
  const engine = loadEngineState() || buildDefaultState();
  const [directorySites, setDirectorySites] = useState(() => loadSites().filter((site) => site?.active !== false));
  const storeOptions = useMemo(() => {
    const fromHeaders = buildStoreOptions(engine?.parsedHeaders || []);
    const fromDirectory = directorySites
      .map((site) => site?.storeKey || site?.inventoryKey || site?.name || site?.siteId || site?.id)
      .map(normalizeStoreValue)
      .filter(Boolean);
    const fromProfiles = loadProfiles().flatMap((profile) => [
      ...(getProfileAllowedSiteIds(profile) || []),
      profile?.store,
    ]).map(normalizeStoreValue).filter((site) => site && site !== 'ALL');
    return Array.from(new Set([
      ...fromHeaders.map(normalizeStoreValue).filter(Boolean),
      ...fromDirectory,
      ...fromProfiles,
    ])).sort((a, b) => formatStoreLabel(a).localeCompare(formatStoreLabel(b)));
  }, [engine, directorySites]);
  const storeOptionsKey = storeOptions.join("|");

  const [profiles, setProfiles] = useState(() => ensureAdminProfile(adminEmail, adminDisplayName));
  const [sections, setSections] = useState(() => activeSections());

  useEffect(() => {
    const legacyPeople = loadPeople();
    if (!legacyPeople.length) return;
    const current = loadProfiles();
    const emails = new Set(current.map((profile) => String(profile.email || "").trim().toLowerCase()).filter(Boolean));
    const names = new Set(current.map((profile) => fullName(profile).trim().toLowerCase()).filter(Boolean));
    const additions = legacyPeople.filter((person) => {
      const email = String(person.email || "").trim().toLowerCase();
      const name = String(person.fullName || "").trim().toLowerCase();
      return !emails.has(email) && !names.has(name);
    }).map((person) => {
      const parts = String(person.fullName || "").trim().split(/\s+/);
      return {
        id: person.id,
        name: parts.shift() || "Person",
        surname: parts.join(" "),
        email: person.email || "",
        whatsapp: person.whatsapp || "",
        operationalRole: person.role || "Stock Controller",
        accessLevel: "standard",
        role: "user",
        pin: "000000",
        store: "",
        allowedSiteIds: [],
        assignedSectionIds: [],
      };
    });
    if (additions.length) {
      saveProfiles([...current, ...additions]);
      setProfiles(ensureAdminProfile(adminEmail, adminDisplayName));
    }
  }, [adminEmail, adminDisplayName]);
  const [adminDraft, setAdminDraft] = useState(null);
  const [adminSaveMessage, setAdminSaveMessage] = useState('');
  const [form, setForm] = useState(() => blankForm());
  const [search, setSearch] = useState("");
  const [openSites, setOpenSites] = useState({ __ADMINS__: true });
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removingProfileId, setRemovingProfileId] = useState("");
  const [removeError, setRemoveError] = useState("");
  const [formDropdownKey, setFormDropdownKey] = useState(0);

  useEffect(() => {
    const next = ensureAdminProfile(adminEmail, adminDisplayName);
    setProfiles(next);
    setOpenSites((prev) => {
      const draft = { ...prev };
      next.forEach((profile) => {
        if (String(profile.role || "").toLowerCase() === "admin") return;
        const site = normalizeStoreValue(profile.store);
        if (!(site in draft)) draft[site] = false;
      });
      return draft;
    });
  }, [storeOptionsKey, adminEmail, adminDisplayName]);

  useEffect(() => {
    function syncDirectorySites() {
      setDirectorySites(loadSites().filter((site) => site?.active !== false));
    }
    syncDirectorySites();
    window.addEventListener(MC_SITE_EVENT, syncDirectorySites);
    window.addEventListener('mc-cloud-sync', syncDirectorySites);
    window.addEventListener('storage', syncDirectorySites);
    return () => {
      window.removeEventListener(MC_SITE_EVENT, syncDirectorySites);
      window.removeEventListener('mc-cloud-sync', syncDirectorySites);
      window.removeEventListener('storage', syncDirectorySites);
    };
  }, []);

  useEffect(() => {
    function syncSections() {
      setSections(activeSections());
    }

    syncSections();
    window.addEventListener(MC_SECTION_EVENT, syncSections);
    window.addEventListener("mc-cloud-sync", syncSections);
    window.addEventListener("storage", syncSections);
    return () => {
      window.removeEventListener(MC_SECTION_EVENT, syncSections);
      window.removeEventListener("mc-cloud-sync", syncSections);
      window.removeEventListener("storage", syncSections);
    };
  }, []);

  const visiblePeople = useMemo(() => profiles.filter((profile) => {
    const email = String(profile?.email || profile?.userEmail || "").trim().toLowerCase();
    if (email === "info@invoicemate.co.za") return false;
    if (!viewerIsPrimaryOwner && (profile?.isPrimaryOwner === true || profile?.primaryOwner === true || profileAccessLevel(profile) === 'owner')) return false;
    return true;
  }), [profiles, viewerIsPrimaryOwner]);

  const filteredProfiles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return visiblePeople;
    return visiblePeople.filter((item) => {
      const name = fullName(item).toLowerCase();
      const site = `${formatStoreLabel(item.store)} ${getProfileAllowedSiteIds(item).map(formatStoreLabel).join(' ')}`.toLowerCase();
      const section = sectionAccessLabel(item, sections).toLowerCase();
      const contact = `${item.email || ""} ${item.whatsapp || ""} ${profileOperationalRole(item)}`.toLowerCase();
      return name.includes(term) || site.includes(term) || section.includes(term) || contact.includes(term);
    });
  }, [visiblePeople, search, sections]);

  const adminProfile = useMemo(
    () => viewerIsPrimaryOwner ? filteredProfiles.find((item) => item.isPrimaryOwner === true) : null,
    [filteredProfiles, viewerIsPrimaryOwner]
  );

  useEffect(() => {
    setAdminDraft(adminProfile ? { ...adminProfile } : null);
    setAdminSaveMessage('');
  }, [adminProfile?.id, adminProfile?.pin]);

  const groupedProfiles = useMemo(() => {
    const groups = {};
    filteredProfiles
      .filter((item) => item.isPrimaryOwner !== true)
      .forEach((item) => {
        const isAdmin = String(item.role || "").toLowerCase() === "admin" || ["administrator", "owner"].includes(profileAccessLevel(item));
        const sites = getProfileAllowedSiteIds(item).filter((site) => site !== 'ALL');
        const targets = isAdmin ? ["__ADMINS__"] : (sites.length ? sites : [normalizeStoreValue(item.store)].filter(Boolean));
        if (!targets.length) targets.push("__UNASSIGNED__");
        targets.forEach((siteId) => {
          const site = ["__UNASSIGNED__", "__ADMINS__"].includes(siteId) ? siteId : normalizeStoreValue(siteId);
          if (!site) return;
          if (!groups[site]) groups[site] = [];
          if (!groups[site].some((profile) => profile.id === item.id)) groups[site].push(item);
        });
      });

    return Object.fromEntries(
      Object.entries(groups).sort((a, b) => groupLabel(a[0]).localeCompare(groupLabel(b[0])))
    );
  }, [filteredProfiles]);

  const siteCount = useMemo(() => Object.keys(groupedProfiles).filter((site) => !["__UNASSIGNED__", "__ADMINS__"].includes(site)).length, [groupedProfiles]);
  const operatorCount = useMemo(() => countUniqueOperators(filteredProfiles), [filteredProfiles]);
  const canManage = canManageProfiles(currentProfile);

  function syncProfiles(nextProfiles) {
    saveProfiles(nextProfiles);
    const refreshed = ensureAdminProfile(adminEmail, adminDisplayName);
    setProfiles(refreshed);
  }

  function createProfile(e) {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{5,6}$/.test(form.pin)) return;
    const next = [
      ...loadProfiles(),
      {
        id: `mc-profile-${Date.now()}`,
        name: form.name.trim(),
        surname: form.surname.trim(),
        email: String(form.email || "").trim().toLowerCase(),
        whatsapp: String(form.whatsapp || "").trim(),
        operationalRole: form.operationalRole || "Stock Controller",
        accessLevel: "standard",
        pin: form.pin,
        store: form.allowedSiteIds[0] || "",
        allowedSiteIds: form.allowedSiteIds,
        allowedSites: form.allowedSiteIds,
        siteIds: form.allowedSiteIds,
        assignedSectionIds: form.assignedSectionIds,
        assignedSections: form.assignedSectionIds,
        sectionIds: form.assignedSectionIds,
        tabs: STANDARD_DEFAULT_TABS,
        role: "user",
        canFinalizeCount: form.canFinalizeCount === true,
        permissions: { canFinalizeCount: form.canFinalizeCount === true },
      },
    ];
    syncProfiles(next);
    setOpenSites((prev) => ({ ...prev, __UNASSIGNED__: true }));
    setForm(blankForm());
    setFormDropdownKey((value) => value + 1);
  }

  function saveAdminDraft() {
    if (!adminDraft) return;
    const nextPin = String(adminDraft.pin || '').trim();
    if (!/^\d{6}$/.test(nextPin)) {
      setAdminSaveMessage('PIN must be exactly 6 digits.');
      return;
    }
    const savedAdmin = {
      ...adminDraft,
      name: String(adminDraft.name || 'Admin').trim() || 'Admin',
      surname: String(adminDraft.surname || '').trim(),
      pin: nextPin,
      email: adminDraft.email || adminEmail || '',
      store: 'ALL',
      allowedSiteIds: ['ALL'],
      assignedSectionIds: ['ALL'],
      whatsapp: String(adminDraft.whatsapp || '').trim(),
      role: 'admin',
    };
    const raw = loadProfiles();
    const next = raw.map((item) => item.id === savedAdmin.id ? { ...item, ...savedAdmin } : item);
    if (!next.some((item) => item.id === savedAdmin.id)) next.unshift(savedAdmin);
    syncProfiles(next);
    setAdminDraft(savedAdmin);
    setAdminSaveMessage('Admin profile saved.');
  }

  function updateProfile(id, patch) {
    const next = profiles.map((item) => {
      if (item.id !== id) return item;
      const isPrimaryOwner = item.isPrimaryOwner === true;
      const safePatch = { ...patch };
      if (isPrimaryOwner) {
        safePatch.accessLevel = 'owner';
        safePatch.operationalRole = 'Owner';
        safePatch.role = 'admin';
        safePatch.isPrimaryOwner = true;
        safePatch.primaryOwner = true;
        // The protected login identity is owned by Super Admin, not Directory.
        safePatch.email = primaryOwnerEmail || item.email || adminEmail || '';
        safePatch.loginInviteRequired = false;
        safePatch.loginAccessStatus = 'primary-owner';
      } else {
        const nextAccess = safePatch.accessLevel || item.accessLevel || (String(item.role || '').toLowerCase() === 'admin' ? 'administrator' : 'standard');
        const nextEmail = String((Object.prototype.hasOwnProperty.call(safePatch, 'email') ? safePatch.email : item.email) || '').trim().toLowerCase();
        const previousEmail = String(item.email || item.userEmail || '').trim().toLowerCase();
        const emailChanged = Boolean(nextEmail && previousEmail && nextEmail !== previousEmail);
        const requiresPersonalLogin = nextAccess === 'administrator' || nextAccess === 'owner';
        const personalLoginActive = item.loginAccessStatus === 'active' && !emailChanged;
        safePatch.role = requiresPersonalLogin ? 'admin' : 'user';
        safePatch.isPrimaryOwner = false;
        safePatch.primaryOwner = false;
        safePatch.loginInviteRequired = requiresPersonalLogin && Boolean(nextEmail) && !personalLoginActive;
        safePatch.loginAccessStatus = requiresPersonalLogin
          ? (nextEmail ? (personalLoginActive ? 'active' : 'pending') : 'shared-login-only')
          : 'contact-only';
        safePatch.loginRequestedAt = safePatch.loginInviteRequired ? new Date().toISOString() : null;
      }
      const merged = { ...item, ...safePatch };
      if (patch.store) {
        merged.store = normalizeStoreValue(patch.store);
        merged.allowedSiteIds = [merged.store];
      }
      if (patch.allowedSiteIds) {
        const allowed = Array.from(new Set((patch.allowedSiteIds || []).map(normalizeStoreValue).filter(Boolean)));
        merged.allowedSiteIds = allowed;
        merged.allowedSites = allowed;
        merged.siteIds = allowed;
        merged.store = allowed[0] || merged.store;
      }
      if (patch.assignedSectionIds) {
        const assigned = Array.from(new Set((patch.assignedSectionIds || []).map(normalizeSectionId).filter(Boolean)));
        merged.assignedSectionIds = assigned;
        merged.assignedSections = assigned;
        merged.sectionIds = assigned;
      }
      return merged;
    });
    syncProfiles(next);
    if (patch.store) {
      const site = normalizeStoreValue(patch.store);
      setOpenSites((prev) => ({ ...prev, [site]: true }));
    }
  }

  function toggleFormSite(siteId) {
    const nextAllowed = toggleSiteInList(form.allowedSiteIds, siteId);
    setForm({ ...form, allowedSiteIds: nextAllowed, store: nextAllowed[0] || form.store });
  }

  function toggleFormSection(sectionId) {
    const nextAssigned = toggleSectionInList(form.assignedSectionIds, sectionId);
    setForm({ ...form, assignedSectionIds: nextAssigned });
  }

  function toggleProfileAccess(profile, siteId) {
    const currentAllowed = getProfileAllowedSiteIds(profile).filter((site) => site !== 'ALL');
    let nextAllowed = toggleSiteInList(currentAllowed, siteId);
    if (!nextAllowed.length) nextAllowed = [normalizeStoreValue(siteId) || normalizeStoreValue(profile.store)].filter(Boolean);
    updateProfile(profile.id, { allowedSiteIds: nextAllowed });
  }

  function toggleProfileSection(profile, sectionId) {
    const currentAssigned = getProfileAllowedSectionIds(profile).filter((section) => section !== 'ALL');
    const nextAssigned = toggleSectionInList(currentAssigned, sectionId);
    updateProfile(profile.id, { assignedSectionIds: nextAssigned });
  }

  function toggleFinalizePermission(profile) {
    const enabled = !(profile?.canFinalizeCount === true || profile?.permissions?.canFinalizeCount === true);
    updateProfile(profile.id, {
      canFinalizeCount: enabled,
      permissions: { ...(profile.permissions || {}), canFinalizeCount: enabled },
    });
  }

  async function removeProfile(id) {
    const target = profiles.find((item) => item.id === id);
    if (!target || target.isPrimaryOwner === true || removingProfileId) return;
    setRemovingProfileId(id);
    setRemoveError("");
    try {
      await mcDeleteProfile(id);
      setProfiles(ensureAdminProfile(adminEmail, adminDisplayName));
      recordActivity({ category: "People", action: "Person removed", title: `Person removed · ${fullName(target)}`, targetId: id, targetName: fullName(target), severity: "attention" });
      setConfirmRemove(null);
    } catch (error) {
      setRemoveError(error?.message || "Could not remove this person. Please try again.");
    } finally {
      setRemovingProfileId("");
    }
  }

  function toggleSite(site) {
    setOpenSites((prev) => ({ ...prev, [site]: !prev[site] }));
  }

  if (!canManage) {
    return (
      <div className="mc-enginePage mc-profilesPage mc-tabPage">
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        <McPageHeader activeKey="profiles" />
        <PeopleSubNav activeTab={peopleTab} onChange={selectPeopleTab} />
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className="mc-enginePage mc-profilesPage mc-tabPage">
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="profiles" />
      <PeopleSubNav activeTab={peopleTab} onChange={selectPeopleTab} />

      {peopleTab === "permissions" ? (
        <PermissionsPeopleWorkspace profiles={profiles} updateProfile={updateProfile} />
      ) : peopleTab === "escalation" ? (
        <EscalationPeopleWorkspace profiles={profiles} onProfilesChange={setProfiles} />
      ) : peopleTab === "activity" ? (
        <ActivityPeopleWorkspace profiles={profiles} />
      ) : peopleTab === "intelligence" ? (
        <PeopleIntelligenceWorkspace profiles={profiles} />
      ) : peopleTab === "directory" ? (
      <>
      <section className="mc-enginePage__heroCards">
        <article className="mc-engineCard">
          <span>Total People</span>
          <strong>{visiblePeople.length}</strong>
        </article>
        <article className="mc-engineCard">
          <span>Sites Covered</span>
          <strong>{siteCount || 0}</strong>
        </article>
        <article className="mc-engineCard">
          <span>Operators</span>
          <strong>{operatorCount}</strong>
        </article>
        <article className="mc-engineCard">
          <span>Sections</span>
          <strong>{sections.length}</strong>
        </article>
      </section>

      <section className="mc-enginePage__content">
        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Add person</h3>
              <p>Create the person once, then assign their site access, sections and operational responsibility in Directory.</p>
            </div>
            <div className="mc-engineBadge">Access</div>
          </div>

          <div style={{ position: "absolute", left: -9999, top: -9999, width: 1, height: 1, overflow: "hidden" }}>
            <input type="text" autoComplete="off" data-lpignore="true" data-1p-ignore="true" tabIndex={-1} />
            <input type="password" autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" tabIndex={-1} />
          </div>

          <form className="mc-fieldGrid" autoComplete="off" onSubmit={createProfile}>
            <div className="mc-fieldControl">
              <label>Name</label>
              <input
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                spellCheck={false}
                name="mc_profile_create_name"
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="mc-fieldControl">
              <label>Surname</label>
              <input
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                spellCheck={false}
                name="mc_profile_create_surname"
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value })}
              />
            </div>

            <div className="mc-fieldControl">
              <label>Email address</label>
              <input type="email" autoComplete="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="person@company.com" />
            </div>

            <div className="mc-fieldControl">
              <label>WhatsApp number</label>
              <input autoComplete="off" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+27..." />
            </div>

            <div className="mc-fieldControl">
              <label>Operational role</label>
              <select value={form.operationalRole} onChange={(e) => setForm({ ...form, operationalRole: e.target.value })}>
                {OPERATIONAL_ROLES.filter((role) => role !== "Owner").map((role) => <option key={role}>{role}</option>)}
              </select>
            </div>

            <div className="mc-fieldControl">
              <label>PIN</label>
              <input
                autoComplete="new-password"
                data-lpignore="true"
                data-1p-ignore="true"
                data-form-type="other"
                type="password"
                name="mc_profile_create_pin"
                minLength={5}
                maxLength={6}
                inputMode="numeric"
                pattern="[0-9]{5,6}"
                readOnly
                onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                value={form.pin}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
              />
            </div>

            <div className="mc-fieldControl">
              <label>Sites</label>
              <details
                key={`sites-${formDropdownKey}`}
                className="mc-profileAccessDropdown mc-profileAccessDropdown--create"
                onToggle={(event) => {
                  if (event.currentTarget.open) setDirectorySites(loadSites().filter((site) => site?.active !== false));
                }}
              >
                <summary><span>{form.allowedSiteIds.length ? `${form.allowedSiteIds.length} site${form.allowedSiteIds.length === 1 ? "" : "s"} selected` : "Select sites"}</span><small>Choose</small></summary>
                <div className="mc-profileAccessDropdown__menu">
                  {!storeOptions.length ? <div className="mc-profileAccessDropdown__empty">No active sites are available yet.</div> : null}
                  {storeOptions.map((option) => {
                    const checked = form.allowedSiteIds.includes(normalizeStoreValue(option));
                    return <button key={option} type="button" onClick={() => toggleFormSite(option)} className={`mc-profileAccessChip ${checked ? "is-selected" : ""}`}>{formatStoreLabel(option)}</button>;
                  })}
                </div>
              </details>
            </div>

            <div className="mc-fieldControl">
              <label>Sections</label>
              <details key={`sections-${formDropdownKey}`} className="mc-profileAccessDropdown mc-profileAccessDropdown--create">
                <summary><span>{form.assignedSectionIds.length ? `${form.assignedSectionIds.length} section${form.assignedSectionIds.length === 1 ? "" : "s"} selected` : "Select sections"}</span><small>Choose</small></summary>
                <div className="mc-profileAccessDropdown__menu">
                  {sections.map((section) => {
                    const sectionId = normalizeSectionId(section.id || section.sectionId || section.name);
                    const checked = form.assignedSectionIds.includes(sectionId);
                    return <button key={sectionId} type="button" onClick={() => toggleFormSection(sectionId)} className={`mc-profileAccessChip ${checked ? "is-selected" : ""}`}>{formatSectionLabel(sectionId, sections)}</button>;
                  })}
                </div>
              </details>
            </div>

            <div className="mc-inlineActions" style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
              <button className="mc-engineBtn mc-engineBtn--primary" type="submit">
                Add person
              </button>
              <button
                className={`mc-engineBtn ${form.canFinalizeCount ? "mc-engineBtn--primary" : "mc-engineBtn--ghost"}`}
                type="button"
                onClick={() => setForm((current) => ({ ...current, canFinalizeCount: !current.canFinalizeCount }))}
                title="Allow this new profile to finalize completed count sessions"
              >
                Finalize Count
              </button>
            </div>
          </form>
        </article>

        <article className="mc-enginePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Live profiles</h3>
              <p>Search by person, site, section, email, or WhatsApp number, then expand the site you want to manage.</p>
            </div>
            <div className="mc-engineBadge">Operators</div>
          </div>

          <div className="mc-fieldControl" style={{ marginBottom: 18 }}>
            <label>Search profiles or sites</label>
            <input
              autoComplete="off"
              data-lpignore="true"
              data-1p-ignore="true"
              spellCheck={false}
              name="mc_profile_manage_search"
              readOnly
              onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, site, or section..."
            />
          </div>

          {adminProfile ? (
            <div className="mc-liveRuleTable mc-profileCards" style={{ marginBottom: 22 }}>
              <div className="mc-profileEditCard mc-profileEditCard--admin">
                <div className="mc-profileEditCard__identity">
                  <input autoComplete="off" value={adminProfile.name || ""} onChange={(e) => updateProfile(adminProfile.id, { name: e.target.value })} placeholder="Owner name" />
                  <input autoComplete="off" value={adminProfile.surname || ""} onChange={(e) => updateProfile(adminProfile.id, { surname: e.target.value })} placeholder="Surname" />
                  <input type="email" value={primaryOwnerEmail || adminProfile.email || ""} readOnly disabled title="Primary Owner email is controlled in Super Admin" placeholder="Primary Owner email" />
                  <input value={adminProfile.whatsapp || ""} onChange={(e) => updateProfile(adminProfile.id, { whatsapp: e.target.value })} placeholder="WhatsApp number" />
                </div>
                <div className="mc-profileEditCard__access">
                  <input value="All Sites" readOnly />
                  <input value="All Sections" readOnly />
                  <input value="Owner · Owner" readOnly title="Primary Owner is protected" />
                  <input type="password" minLength={5} maxLength={6} inputMode="numeric" pattern="[0-9]{5,6}" value={adminProfile.pin || ""} onChange={(e) => updateProfile(adminProfile.id, { pin: e.target.value.replace(/\D/g, "").slice(0, 6) })} placeholder="6-digit PIN" />
                </div>
                <div className="mc-profileEditCard__actions">
                  <button className={`mc-engineBtn ${adminProfile.permissions?.canFinalizeCount || adminProfile.canFinalizeCount ? "mc-engineBtn--primary" : "mc-engineBtn--ghost"}`} type="button" onClick={() => toggleFinalizePermission(adminProfile)}>Finalize Count</button>
                  <span className="mc-engineBadge">Primary Owner</span>
                  <small className="mc-autoSaveNote">Owner email is locked to Super Admin · other changes save automatically</small>
                </div>
              </div>
            </div>
          ) : null}

          {Object.entries(groupedProfiles).map(([site, items]) => {
            const isOpen = !!openSites[site];
            return (
              <div key={site} style={{ marginTop: 18 }}>
                <button
                  type="button"
                  onClick={() => toggleSite(site)}
                  className="mc-profileSiteToggle"
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "rgba(4,18,38,.78)",
                    border: "1px solid rgba(103,232,249,.14)",
                    borderRadius: 18,
                    color: "#e2e8f0",
                    padding: "16px 18px",
                    cursor: "pointer",
                    marginBottom: isOpen ? 12 : 0,
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{groupLabel(site)}</div>
                    <div style={{ marginTop: 4, fontSize: 13, color: "#94a3b8" }}>
                      {items.length} profile{items.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div style={{ fontSize: 18 }}>{isOpen ? "−" : "+"}</div>
                </button>

                {isOpen ? (
                  <div className="mc-liveRuleTable mc-profileCards">
                    {items.map((profile) => (
                      <div className="mc-profileEditCard" key={profile.id}>
                        <div className="mc-profileEditCard__identity">
                          <input
                            autoComplete="off"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            spellCheck={false}
                            name={`mc_profile_name_${profile.id}`}
                            readOnly
                            onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                            value={profile.name || ""}
                            onChange={(e) => updateProfile(profile.id, { name: e.target.value })}
                            placeholder="Name"
                          />
                          <input
                            autoComplete="off"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            spellCheck={false}
                            name={`mc_profile_surname_${profile.id}`}
                            readOnly
                            onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                            value={profile.surname || ""}
                            placeholder="Surname"
                            onChange={(e) => updateProfile(profile.id, { surname: e.target.value })}
                            style={{ marginTop: 10 }}
                          />
                          <input type="email" value={profile.email || ""} placeholder="Email address" onChange={(e) => updateProfile(profile.id, { email: e.target.value.trim().toLowerCase() })} style={{ marginTop: 10 }} />
                          <input value={profile.whatsapp || ""} placeholder="WhatsApp number" onChange={(e) => updateProfile(profile.id, { whatsapp: e.target.value })} style={{ marginTop: 10 }} />
                        </div>

                        <div className="mc-profileEditCard__access">
                          <details className="mc-profileAccessDropdown">
                            <summary>
                              <span>{siteAccessSummary(profile)}</span>
                              <small>Change</small>
                            </summary>
                            <div className="mc-profileAccessDropdown__menu">
                              {!storeOptions.length ? <div className="mc-profileAccessDropdown__empty">{siteAccessLabel(profile)}</div> : null}
                              {storeOptions.map((option) => {
                                const checked = getProfileAllowedSiteIds(profile).includes(normalizeStoreValue(option));
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    onClick={() => toggleProfileAccess(profile, option)}
                                    className={`mc-profileAccessChip ${checked ? "is-selected" : ""}`}
                                  >
                                    {formatStoreLabel(option)}
                                  </button>
                                );
                              })}
                            </div>
                          </details>
                          <details className="mc-profileAccessDropdown">
                            <summary>
                              <span>{sectionAccessSummary(profile, sections)}</span>
                              <small>Change</small>
                            </summary>
                            <div className="mc-profileAccessDropdown__menu">
                              {!sections.length ? <div className="mc-profileAccessDropdown__empty">{sectionAccessLabel(profile, sections)}</div> : null}
                              {sections.map((section) => {
                                const sectionId = section.sectionId || section.id;
                                const checked = getProfileAllowedSectionIds(profile).includes(normalizeSectionId(sectionId));
                                return (
                                  <button
                                    key={sectionId}
                                    type="button"
                                    onClick={() => toggleProfileSection(profile, sectionId)}
                                    className={`mc-profileAccessChip ${checked ? "is-selected" : ""}`}
                                  >
                                    {section.name || formatSectionLabel(sectionId)}
                                  </button>
                                );
                              })}
                            </div>
                          </details>
                          <input
                            autoComplete="new-password"
                            data-lpignore="true"
                            data-1p-ignore="true"
                            data-form-type="other"
                            type="password"
                            name={`mc_profile_pin_${profile.id}`}
                            minLength={5}
                            maxLength={6}
                            inputMode="numeric"
                            pattern="[0-9]{5,6}"
                            readOnly
                            onFocus={(e) => e.currentTarget.removeAttribute("readOnly")}
                            value={profile.pin || ""}
                            onChange={(e) => updateProfile(profile.id, { pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                          />
                          {profile.isPrimaryOwner === true ? (
                            <input value="Owner · Owner" readOnly title="Primary Owner is protected" />
                          ) : (
                            <div className="mc-profileRoleEditors">
                              <select
                                value={profileAccessLevel(profile)}
                                onChange={(e) => {
                                  const accessLevel = e.target.value;
                                  updateProfile(profile.id, {
                                    accessLevel,
                                    role: accessLevel === "owner" || accessLevel === "administrator" ? "admin" : "user",
                                    tabs: defaultTabsForAccess(accessLevel),
                                    assignedSectionIds: accessLevel === "administrator" ? ["ALL"] : getProfileAllowedSectionIds(profile),
                                  });
                                }}
                                aria-label={`Access level for ${fullName(profile)}`}
                              >
                                {ACCESS_LEVELS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                              </select>
                              <select
                                value={profileOperationalRole(profile)}
                                onChange={(e) => updateProfile(profile.id, { operationalRole: e.target.value })}
                                aria-label={`Operational role for ${fullName(profile)}`}
                              >
                                {OPERATIONAL_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                              </select>
                            </div>
                          )}
                        </div>

                        <div className="mc-profileEditCard__actions" style={{ display: "flex", flexDirection: "column", alignItems: "stretch", justifyContent: "center", gap: 10, minWidth: 132 }}>
                          <button
                            className={`mc-engineBtn ${profile.canFinalizeCount === true || profile.permissions?.canFinalizeCount === true ? "mc-engineBtn--primary" : "mc-engineBtn--ghost"}`}
                            type="button"
                            onClick={() => toggleFinalizePermission(profile)}
                            title={profile.canFinalizeCount === true || profile.permissions?.canFinalizeCount === true ? "This profile can finalize counts" : "This profile cannot finalize counts"}
                            style={{ width: "100%", minWidth: 132, padding: "10px 14px", lineHeight: 1.15 }}
                          >
                            Finalize Count
                          </button>
                          <small className="mc-autoSaveNote">Changes save automatically</small>
                          {(["administrator", "owner"].includes(profileAccessLevel(profile))) ? (
                            <small className={`mc-autoSaveNote ${profile.loginAccessStatus === "active" ? "is-active" : ""}`}>
                              {profile.loginAccessStatus === "active" ? "Personal login active" : profile.email ? "Login invitation pending in Super Admin" : "Shared login only · add email for personal login"}
                            </small>
                          ) : null}
                          <button
                            className="mc-engineBtn mc-engineBtn--ghost"
                            type="button"
                            onClick={() => { setRemoveError(""); setConfirmRemove(profile); }}
                            style={{ width: "100%", minWidth: 132, padding: "10px 14px" }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </article>
      </section>

      </>
      ) : (
        <PeopleScaffold tab={peopleTab} />
      )}

      {confirmRemove ? (
        <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div
            onClick={() => setConfirmRemove(null)}
            style={{ position: "absolute", inset: 0, background: "rgba(2,6,23,.62)", backdropFilter: "blur(12px)" }}
          />
          <div
            style={{
              position: "relative",
              width: "min(520px, 92vw)",
              borderRadius: 28,
              padding: 26,
              border: "1px solid rgba(103,232,249,.16)",
              background: "linear-gradient(180deg, rgba(8,20,40,.96), rgba(4,13,27,.99))",
              boxShadow: "0 30px 120px rgba(0,0,0,.55)",
            }}
          >
            <div className="mc-enginePage__eyebrow">Person removal</div>
            <h3 style={{ margin: "0 0 8px" }}>Remove {fullName(confirmRemove)}?</h3>
            {removeError ? <p role="alert" style={{ color: "#f87171" }}>{removeError}</p> : null}
            <p style={{ margin: "0 0 18px", color: "#9ab2ca" }}>
              This will remove the profile from {siteAccessLabel(confirmRemove)} and its assigned sections. Their previous count history will remain available.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={() => setConfirmRemove(null)}>
                Cancel
              </button>
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" disabled={Boolean(removingProfileId)} onClick={() => removeProfile(confirmRemove.id)}>
                {removingProfileId ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}
